import { HttpErrorResponse } from '@angular/common/http';
import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, HostListener, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Observable, of, Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, finalize, map, shareReplay, take, takeUntil } from 'rxjs/operators';
import { QuestionTypeAllItems } from '../../../../meta-data/app-meta-data.service';
import { confidenceLabel, weakAreas } from '../../../quizzes/components/quiz-builder-pro-page/quiz-builder-pro.util';
import { AcsElement, ProCourse, ProModule } from '../../../quizzes/models/quiz-builder-pro.model';
import { QuizBuilderProService } from '../../../quizzes/services/quiz-builder-pro.service';
import { DEMO_SCENARIO, KIND_GROUPS, QUESTION_KINDS, STARTER_FLOWS, kindInfo, sampleQuestion } from '../../models/scenario-demo-data';
import { QuestionKind, QuestionKindInfo, Scenario, ScenarioPanelState, ScenarioPhrase, ScenarioQuestion, ScenarioStep, ScenarioImageExample, ScenarioTarget, StepType } from '../../models/scenario.model';
import { ScenarioConfirmation } from '../../models/scenario-confirmation.model';
import { ScenarioDropdownOption } from '../../models/scenario-dropdown.model';
import { ScenarioFlowFormatVm, ScenarioFlowRequest, ScenarioFlowStructure, ScenarioFlowSuggestion } from '../../models/scenario-flow-ai.model';
import { ClozeBlankRow, ClozeDistractorRow, ClozeEditorResult, ClozePreviewPart } from '../../models/scenario-cloze-editor.model';
import { attachScenarioCoaching } from '../../services/scenario-coaching';
import { buildClozeEditor, loadClozeEditor, replaceClozeBlank, validClozeQuestion } from '../../services/scenario-cloze-editor';
import { ScenarioDropdownComponent } from '../scenario-dropdown/scenario-dropdown.component';
import { PRACTICE_AI, PRACTICE_SCENARIO } from '../../models/scenario-practice-data';
import { ScenarioToastService } from '../../services/scenario-toast.service';
import { ScenarioImageService } from '../../services/scenario-image.service';
import { BankQuestionItem, ScenarioQuestionBankService } from '../../services/scenario-question-bank.service';
import { scenarioAiError, ScenarioQuestionAiService, ScenarioQuestionDraft, ScenarioQuestionTarget } from '../../services/scenario-question-ai.service';

import { COCKPIT_PANELS, ScenarioCockpitPanel } from '../../models/scenario-cockpit-panels';

type View = 'flows' | 'library' | 'builder';

/** Output ports map 1:1 to the step's transition fields. */
type PortKey = 'next' | 'onCorrect' | 'onIncorrect';
interface PortVm { key: PortKey; label: string; color: string; target: string; connected: boolean; }
interface NodeVm {
  id: string; code: string; typeLabel: string; accent: string; icon: string | null; title: string; meta: string; ariaLabel: string; x: number; y: number;
  thumb: string | null;
  question: boolean; coaching: ScenarioStep['coaching'] | null; coachingOpen: boolean; coachingLabel: string;
  coachingAiLabel: string; coachingAiDisabled: boolean; coachingAiError: string;
  selected: boolean; primary: boolean; isStart: boolean; warn: boolean; fromBank: boolean; filled: boolean; ports: PortVm[];
}
interface EdgeVm { id: string; d: string; color: string; dash: string; label: string; lx: number; ly: number; x2: number; y2: number; selected: boolean; }
interface QuickAddVm { left: number; top: number; cx: number; cy: number; from?: { id: string; port: PortKey }; }
interface BoxVm { left: number; top: number; width: number; height: number; }
interface IssueVm { stepId: string; code: string; text: string; }
interface BankRowVm extends BankQuestionItem { inFlowLabel: string; addKind: QuestionKind; kindOptions: QuestionKindInfo[]; added: boolean; }
interface SavedQuestion { id: string; question: ScenarioQuestion; kindName: string; }
interface SavedFlow { id: string; scenario: Scenario; savedAt: number; }
interface FlowCardVm { flow: SavedFlow; id: string; title: string; subtitle: string; meta: string; current: boolean; }
interface DraftVm { title: string; meta: string; note: string; }
interface TargetOptionVm { id: string; label: string; }
/** Phrase row for the radio editor: the dropdown speaks string ids, the model stores a numeric element index. */
interface PhraseRowVm { index: number; phrase: ScenarioPhrase; elementId: string; }

const STORAGE_KEY = 'aviator-scenario-builder-v1';
const QUESTIONS_KEY = 'aviator-scenario-questions-v1';
const FLOWS_KEY = 'aviator-scenario-flows-v1';
/** Saved flow the working draft was opened from (absent for unsaved / template copies). */
const DRAFT_FLOW_KEY = 'aviator-scenario-draft-flow-v1';
const NODE_W = 232;
const ANCHOR_Y = 46;
/** Vertical center of the input port, measured from the node top. */
const IN_Y = 22;
const GRID = 20;
const HISTORY_MAX = 100;
const PORT_COLORS: { [k in PortKey]: string } = { next: '#9ca3af', onCorrect: '#3d9a5f', onIncorrect: '#c44545' };
const ACCENTS: { [t: string]: string } = { narrative: '#0e7490', question: '#7659b8', remediation: '#d49b14', debrief: '#9d3d6b' };
const STEP_ICONS: { [t: string]: string } = { narrative: 'fa-file-alt', remediation: 'fa-medkit', debrief: 'fa-flag-checkered' };
const TYPE_LABELS: { [t: string]: string } = { narrative: 'Narrative', question: 'Question', remediation: 'Coaching', debrief: 'Debrief' };
type ImageTab = 'upload' | 'url' | 'ai';
type InspectorTab = 'content' | 'paths';
interface InspectorTabVm {
  key: InspectorTab; label: string; id: string; panelId: string; count: number; warn: boolean; image: boolean;
}
interface InspectorRouteVm { label: string; destination: string; tone: string; icon: string; }
const IMAGE_EXAMPLES: Record<Exclude<StepType, 'question'>, ScenarioImageExample[]> = {
  narrative: [{ url: 'assets/scenarios/examples/narrative-ai-example.png', label: 'Cockpit context',
    alt: 'Illustration of a pilot flying over farmland with a runway ahead' }],
  remediation: [{ url: 'assets/scenarios/examples/remediation-ai-example.png', label: 'Instructor coaching',
    alt: 'Illustration of an instructor reviewing a checklist with a student in a parked airplane' }],
  debrief: [{ url: 'assets/scenarios/examples/debrief-ai-example.png', label: 'Post-flight reflection',
    alt: 'Illustration of a student and instructor reviewing a flight beside a parked airplane' }],
};
const IMAGE_EXAMPLE_URLS = new Set(Object.values(IMAGE_EXAMPLES).reduce((urls, examples) => urls.concat(examples.map(e => e.url)), [] as string[]));
const IMAGE_PROMPT_STYLE: Partial<Record<StepType, string>> = {
  narrative: 'An immersive establishing scene that introduces the situation',
  remediation: 'A calm instructional scene illustrating reflection and coaching',
  debrief: 'A thoughtful post-flight scene illustrating review and learning',
};
const CHOICE_KINDS: QuestionKind[] = ['multiple-choice', 'multiple-response', 'timed', 'confidence', 'answer-rationale', 'panel-read', 'audio-cue'];
const HOTSPOT_KINDS: QuestionKind[] = ['hazard-audit', 'cockpit-control'];
/** Diversion airports live in the student map's SVG viewBox (scenario-question.component.ts). */
const MAP_W = 860;
const MAP_H = 360;
/** Same fallback the student panel uses when a question has no panel state yet. */
const PANEL_DEFAULTS: ScenarioPanelState = { ias: 100, alt: 3000, vsi: 0, pitch: 0, bank: 0, hdg: 360 };
const MAX_IMAGE_W = 1600;
const NUDGE: { [key: string]: [number, number] } = { ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1] };
/** Matches the SCSS breakpoint where the settings panel becomes a bottom sheet. */
const MOBILE_QUERY = '(max-width: 800px)';
const LONG_PRESS_MS = 500;
/** Finger travel (px) that turns a tap / long-press into a pan. */
const TAP_SLOP = 8;
const QUICK_W = 260;

@Component({
  selector: 'app-scenario-builder-page',
  templateUrl: './scenario-builder-page.component.html',
  styleUrls: ['./scenario-builder-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ScenarioToastService],
})
export class ScenarioBuilderPageComponent implements OnInit, AfterViewInit, OnDestroy {
  /** A separate, in-memory editor instance used only by the visual walkthrough. */
  @Input() demonstration = false;
  walkthroughOpen = false;
  private walkthroughTrigger: HTMLElement | null = null;
  confirmation: ScenarioConfirmation | null = null;
  private confirmationAction: (() => void) | null = null;
  private confirmationTrigger: HTMLElement | null = null;
  view: View = 'flows';
  layout: 'wide' | 'regular' | 'compact' = 'compact';
  private layoutObserver?: { observe(element: HTMLElement): void; disconnect(): void };
  scenario: Scenario = clone(DEMO_SCENARIO);
  readonly starterPresets = STARTER_FLOWS.map(p => ({ ...p, meta: flowMeta(p.scenario) }));
  flowMenuOpen = false;
  flowMenuLevel: 'root' | 'presets' = 'root';
  savedFlows: SavedFlow[] = [];
  flowCards: FlowCardVm[] = [];
  draft: DraftVm | null = null;
  flowId: string | null = null;
  flowDirty = true;
  playScenario: Scenario = this.scenario;
  playStart: string | null = null;
  previewOpen = false;

  nodes: NodeVm[] = [];
  edges: EdgeVm[] = [];
  issues: IssueVm[] = [];
  issuesOpen = false;
  addMenuOpen = false;
  zoom = 1;
  canvasTransform = 'translate(40px, 40px) scale(1)';
  gridSize = '20px 20px';
  gridPosition = '40px 40px';
  canvasW = 2200;
  canvasH = 900;
  selectionCount = 0;
  wireD = '';
  wireColor = '';
  wireTargetId: string | null = null;
  /** Connection highlighted while an unlinked step is dragged over it; releasing splices the step into it. */
  spliceEdgeId: string | null = null;
  spliceOutD = '';
  spliceColor = '';
  edgeDel: { id: string; x: number; y: number } | null = null;
  quickAdd: QuickAddVm | null = null;
  box: BoxVm | null = null;
  canUndo = false;
  canRedo = false;

  selected: ScenarioStep | null = null;
  selectedCode = '';
  selectedKindInfo: QuestionKindInfo | null = null;
  selectedHasChoices = false;
  selectedHasWhys = false;
  selectedHasHotspots = false;
  selectedPhraseRows: PhraseRowVm[] = [];
  selectedPhraseOptions: ScenarioDropdownOption[] = [];
  selectedSingleSpot = false;
  activeSpot = -1;
  imageDragOver = false;
  readonly imageTabs: { key: ImageTab; label: string; icon: string }[] = [
    { key: 'upload', label: 'Upload', icon: 'fa-upload' },
    { key: 'url', label: 'Link', icon: 'fa-link' },
    { key: 'ai', label: 'Generate', icon: 'fa-magic' },
  ];
  imageTab: ImageTab = 'upload';
  selectedImageExamples: ScenarioImageExample[] = [];
  selectedImageIsDemo = false;
  selectedImageLink = '';
  selectedTypeLabel = '';
  inspectorTab: InspectorTab = 'content';
  inspectorTabs: InspectorTabVm[] = [];
  inspectorRoutes: InspectorRouteVm[] = [];
  inspectorBranching = false;
  coachingOpenId: string | null = null;
  coachingGeneratingId: string | null = null;
  coachingErrorId: string | null = null;
  coachingError = '';
  selectedCanGenerateCoaching = false;
  private coachingRequest?: Subscription;
  imageDrawerOpen = false;
  inspectorImageUrl = '';
  inspectorImageAlt = '';
  readonly questionImageTabs = this.imageTabs.slice(0, 2);
  private inspectorQuestionTab: InspectorTab = 'content';
  private inspectorContentTab: InspectorTab = 'content';
  private inspectorFamily: 'question' | 'content' = 'content';
  promptCopied = false;
  imageMessage = '';
  imageGenerating = false;
  generatedImageUrl = '';
  private generatedImageStep: ScenarioStep | null = null;

  targetOptions: { id: string | undefined; label: string }[] = [];
  branchOptions: { id: string | undefined; label: string }[] = [];
  branchDropdownOptions: ScenarioDropdownOption[] = [];
  readonly viewOptions: ScenarioDropdownOption[] = [
    {
      id: 'builder', label: 'Flow',
      iconPath: 'M9 19a3 3 0 1 1-6 0a3 3 0 1 1 6 0Z M9 19h8.5a3.5 3.5 0 0 0 0-7h-11a3.5 3.5 0 0 1 0-7H15 M21 5a3 3 0 1 1-6 0a3 3 0 1 1 6 0Z',
    },
    {
      id: 'library', label: 'Question types',
      iconPath: 'M4 3h5a1 1 0 0 1 1 1v5a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1Z M4 14h5a1 1 0 0 1 1 1v5a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1v-5a1 1 0 0 1 1-1Z M14 4h7 M14 9h7 M14 15h7 M14 20h7',
    },
  ];
  questionJson = '';
  jsonError = '';

  readonly kindGroups = KIND_GROUPS.map(g => ({ ...g, label: `${g.tag} · ${g.name}`, groupClass: `is-group-${g.tag}`, kinds: QUESTION_KINDS.filter(k => k.group === g.tag) }));
  readonly libraryTotal = QUESTION_KINDS.length;
  libraryGroups = this.kindGroups;
  collapsedGroups: { [tag: string]: boolean } = {};
  libraryGroup = 'all';
  librarySearch = '';
  libraryResults = this.libraryTotal;
  readonly stepTypes: { type: StepType; label: string; icon: string; accent: string }[] = [
    { type: 'narrative', label: 'Narrative', icon: STEP_ICONS.narrative, accent: ACCENTS.narrative },
    { type: 'debrief', label: 'Debrief', icon: STEP_ICONS.debrief, accent: ACCENTS.debrief },
  ];
  libraryKind: QuestionKind = 'multiple-response';
  libraryInfo: QuestionKindInfo = kindInfo('multiple-response');
  libraryQuestion = sampleQuestion('multiple-response');
  clozeText = '';
  clozeBlanks: ClozeBlankRow[] = [];
  clozeDistractors: ClozeDistractorRow[] = [];
  clozePreview: ClozePreviewPart[] = [];
  clozeBank: string[] = [];
  clozeSwapOptions: ScenarioDropdownOption[] = [];
  clozeErrors: string[] = [];
  private clozeLoadedQuestion: ScenarioQuestion;
  private clozeSnapshot = '';
  readonly cockpitPanels = COCKPIT_PANELS;
  readonly hotspotModelOptions = { standalone: true };
  libraryHotspotContext = { $implicit: this.libraryQuestion };
  selectedHotspotContext: { $implicit: ScenarioQuestion | null } = { $implicit: null };
  libraryPreviewQuestion = clone(this.libraryQuestion);
  libraryJson = JSON.stringify(this.libraryQuestion, null, 2);
  libraryJsonError = '';
  libraryHasChoices = CHOICE_KINDS.includes(this.libraryKind);
  libraryHasHotspots = HOTSPOT_KINDS.includes(this.libraryKind);
  libraryPhraseRows: PhraseRowVm[] = [];
  libraryPhraseOptions: ScenarioDropdownOption[] = [];
  libraryMultiple = this.libraryKind === 'multiple-response';
  libraryCanAdd = false;
  libraryStatus: 'new' | 'dirty' | 'saved' = 'new';
  libraryPane: 'types' | 'edit' | 'preview' = 'edit';
  libraryRail: 'types' | 'saved' = 'types';
  librarySavedResults: SavedQuestion[] = [];
  libraryQuestionId: string | null = null;
  savedQuestions: SavedQuestion[] = [];
  librarySaveError = '';
  libraryDirty = false;
  /** AI drafting covers the A (Quiz formats) and B (Interactive exercises) collections. */
  libraryCanDraft = true;
  aiTopic = '';
  aiGenerating = false;
  readonly ditherDots = Array.from({ length: 25 }, (_, i) => i);
  aiError = '';
  /** Source note for the draft now in the fields; cleared once the author saves or switches questions. */
  aiDraftMeta = '';
  /** The editor holds real content (saved, edited or drafted), so the next draft is another version of it. */
  aiHasContext = false;
  /** JSON of the question as AI last filled it, so drafting again over untouched AI output needs no confirmation. */
  private aiFilledJson = '';
  private aiRequest?: Subscription;
  /** The walkthrough releases the prepared draft once its loading state has been seen; see finishDemonstrationDraft. */
  private demonstrationDraft?: Subject<void>;
  /** Course, module and ACS targets from CAP metadata, loaded like Quiz Builder Pro; a course is required to draft. */
  aiCourseOptions: TargetOptionVm[] = [];
  /** Resolved from the flow's locked target by syncTarget; never edited directly. */
  aiCourseId: string | undefined;
  aiModuleId: string | undefined;
  /** Applied ACS picks; starts on the course's top FAA written-test misses, the weak areas Quiz Builder Pro leads with. */
  aiAcsCodes: string[] = [];
  aiCoursesLoading = false;
  aiAcsLoading = false;
  aiTargetError = '';
  aiAcsNote = '';
  aiTargetReady = false;
  /** The course's ACS knowledge and risk elements, with Area of Operation and Task names for the picker. */
  aiElements: AcsElement[] = [];
  private aiCourses: ProCourse[] = [];
  private aiModules: ProModule[] = [];
  private aiCoursesLoaded = false;
  private aiAcsRequest?: Subscription;
  /** The flow's locked course & module, precomputed for the header chip and the Draft with AI line. */
  targetSet = false;
  targetStale = false;
  targetLabel = 'No course';
  targetLine = 'This flow has no course yet.';
  targetNote = '';
  targetAction = 'Choose course & module';
  /** Course & module gate: 'new' runs before a new flow opens, 'change' edits the open flow's target. */
  gateMode: 'new' | 'change' | null = null;
  gateDescription = '';
  gateCourseId = '';
  gateModuleId = '';
  gateModuleOptions: TargetOptionVm[] = [];
  gateReady = false;
  gateCanSkip = false;
  gateCompose = false;
  gatePrompt = '';
  gateSuggestions: ScenarioFlowSuggestion[] = [];
  gateStructure: ScenarioFlowStructure = 'ai';
  gateBranchPreview = true;
  gateQuestionRouteLabel = '';
  gateStructureNote = '';
  gateFormats: ScenarioFlowFormatVm[] = [];
  gateTotal = 0;
  gateCanGenerate = false;
  gateBlocker = '';
  gateGenerating = false;
  gateError = '';
  gateAcsElements: AcsElement[] = [];
  gateAcsCodes: string[] = [];
  gateAcsLoading = false;
  gateAcsNote = '';
  gateAcsFailed = false;
  private gateAcsRequest?: Subscription;
  private gateAcsSource?: Observable<AcsElement[]>;
  private gateAcsCourseId?: number;
  private nextTargetAcsSource?: Observable<AcsElement[]>;
  private gateGenerationRequest?: Subscription;
  private gatePending: { scenario: Scenario; view: View } | null = null;
  private gateTrigger: HTMLElement | null = null;

  bankOpen = false;
  bankRows: BankRowVm[] = [];
  bankTotal = 0;
  bankHasMore = false;
  bankLoading = false;
  bankError = '';
  bankTerm = '';
  bankType: number | null = null;
  readonly bankTypes = [
    { id: null, label: 'All types' },
    { id: QuestionTypeAllItems.MultipleChoice, label: 'Multiple choice' },
    { id: QuestionTypeAllItems.TrueOrFalse, label: 'True / False' },
    { id: QuestionTypeAllItems.MultipleAnswer, label: 'Multiple answer' },
  ];

  private bankPage = 1;
  private bankItems: BankQuestionItem[] = [];
  private readonly search$ = new Subject<string>();
  private readonly destroy$ = new Subject<void>();
  private drag: { id: string; starts: Map<string, { x: number; y: number }>; startX: number; startY: number; moved: boolean; toggled: boolean } | null = null;
  private pan: { x: number; y: number; panX: number; panY: number } | null = null;
  /** Touch pointers down on empty canvas: one finger pans, two pinch-zoom. */
  private readonly touches = new Map<number, { x: number; y: number }>();
  private touchGesture: { startX: number; startY: number; moved: boolean; pinched: boolean; longPressed: boolean } | null = null;
  private pinch: { dist: number; midX: number; midY: number } | null = null;
  private longPressTimer: ReturnType<typeof setTimeout> | null = null;
  private sheetDrag: { el: HTMLElement; y: number; t: number; dy: number } | null = null;
  private wire: { from: string; port: PortKey } | null = null;
  private boxDrag: { x: number; y: number; base: string[] } | null = null;
  private selectedIds = new Set<string>();
  private selectedEdge: string | null = null;
  private readonly portY = new Map<string, number>();
  private readonly nodeH = new Map<string, number>();
  private measureQueued = false;
  private history: string[] = [];
  /** ACS picks per history entry (session-only; null while that entry was recorded before its course's codes loaded). */
  private historyAcs: (string[] | null)[] = [];
  /** Picks a restore wants reapplied once its ACS elements arrive (or at once when the course is unchanged). */
  private pendingAcs: string[] | null = null;
  /** The course pendingAcs belongs to; picks never apply to another course's load. */
  private pendingAcsCourseId: number | undefined;
  /** Restore picks captured by the ACS load currently in flight; edits recorded during it snapshot these. */
  private inFlightAcsPicks: string[] | null = null;
  private historyIndex = -1;
  private lastRecord = 0;
  private panX = 40;
  private panY = 40;
  panning = false;
  @ViewChild('confirmationDialog', { static: true }) private confirmationDialog?: ElementRef<HTMLDialogElement>;
  @ViewChild('flowTargetDialog', { static: true }) private flowTargetDialog?: ElementRef<HTMLDialogElement>;
  @ViewChild('gatePromptInput') private gatePromptInput?: ElementRef<HTMLTextAreaElement>;
  @ViewChild('gateCourse') private gateCourse?: ScenarioDropdownComponent;
  @ViewChild('gateCancel') private gateCancel?: ElementRef<HTMLButtonElement>;
  @ViewChild('walkthroughDialog', { static: true }) private walkthroughDialog?: ElementRef<HTMLDialogElement>;
  @ViewChild('newFlowBtn') private newFlowBtn?: ElementRef<HTMLButtonElement>;
  @ViewChild('libraryHeading') private libraryHeading?: ElementRef<HTMLElement>;
  @ViewChild('confirmationCancel') private confirmationCancel?: ElementRef<HTMLButtonElement>;
  @ViewChild('confirmationAccept') private confirmationAccept?: ElementRef<HTMLButtonElement>;
  @ViewChild('builderRoot', { static: true }) private builderRoot?: ElementRef<HTMLElement>;
  @ViewChild('viewport') private viewport?: ElementRef<HTMLElement>;
  @ViewChild('inspectorBody') private inspectorBody?: ElementRef<HTMLElement>;
  @ViewChild('inspectorJsonDialog') private inspectorJsonDialog?: ElementRef<HTMLDialogElement>;
  @ViewChild('inspectorImageDialog') private inspectorImageDialog?: ElementRef<HTMLDialogElement>;
  @ViewChild('flowMenuBtn') private flowMenuBtn?: ElementRef<HTMLButtonElement>;
  @ViewChild('flowPresetsItem') private flowPresetsItem?: ElementRef<HTMLButtonElement>;
  @ViewChild('flowMenu') private flowMenu?: ElementRef<HTMLElement>;
  private spotDrag: { i: number; stage: Element; moved: boolean; question: ScenarioQuestion } | null = null;
  /** JSON of the open flow as last saved; the draft is dirty while it differs. */
  private savedSnapshot = '';
  private hasDraft = false;
  private aiToastId: string | null = null;

  constructor(
    private readonly bank: ScenarioQuestionBankService,
    private readonly cdr: ChangeDetectorRef,
    private readonly images: ScenarioImageService,
    private readonly questionAi: ScenarioQuestionAiService,
    private readonly quizPro: QuizBuilderProService,
    private readonly toast: ScenarioToastService,
  ) { }

  ngOnInit(): void {
    this.toast.enabled = !this.demonstration;
    if (this.demonstration) {
      this.scenario = { title: 'Untitled scenario', subtitle: '', steps: [] };
      this.view = 'builder';
      this.loadAiCourses();
    } else {
      this.load();
      this.loadQuestions();
      this.loadFlows();
      this.loadAiCourses();
      this.search$.pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$)).subscribe(() => this.loadBank(true));
    }
    this.syncTarget();
    this.record();
    this.rebuild();
    // On phones the settings panel is a bottom sheet, so start with the canvas unobstructed.
    if (!window.matchMedia?.(MOBILE_QUERY).matches) { this.select(this.scenario.steps[0]?.id); }
  }

  ngAfterViewInit(): void {
    this.scheduleMeasure();
    this.updateLayout();
    // TS 4.0 does not include ResizeObserver in lib.dom; keep the compatibility type at this boundary.
    const Observer = (window as any).ResizeObserver;
    if (Observer && this.builderRoot) {
      this.layoutObserver = new Observer(() => this.updateLayout());
      this.layoutObserver.observe(this.builderRoot.nativeElement);
    }
  }

  @HostListener('window:resize')
  updateLayout(): void {
    const width = this.builderRoot?.nativeElement.clientWidth;
    if (!width) { return; }
    const next = width >= 1360 ? 'wide' : width >= 960 ? 'regular' : 'compact';
    if (next === this.layout) { return; }
    this.layout = next;
    if (next === 'compact' && this.selectionCount) { this.bankOpen = false; }
    // Types is a separate pane only in compact mode.
    if (next !== 'compact' && this.libraryPane === 'types') { this.libraryPane = 'edit'; }
    this.cdr.detectChanges();
  }

  ngOnDestroy(): void {
    this.toast.dismiss();
    this.walkthroughDialog?.nativeElement.close();
    this.confirmationAction = null;
    this.confirmationDialog?.nativeElement.close();
    this.gateMode = null;
    this.flowTargetDialog?.nativeElement.close();
    this.layoutObserver?.disconnect();
    this.cancelLongPress();
    this.coachingRequest?.unsubscribe();
    this.aiRequest?.unsubscribe();
    this.aiAcsRequest?.unsubscribe();
    this.gateAcsRequest?.unsubscribe();
    this.gateGenerationRequest?.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
  }

  /** Page toasts wait until every native modal has left the top layer. */
  toastDialogClosed(): void {
    const dialogs = [this.walkthroughDialog, this.confirmationDialog, this.flowTargetDialog,
      this.inspectorJsonDialog, this.inspectorImageDialog];
    this.toast.setHeld(dialogs.some(dialog => !!dialog?.nativeElement.open));
  }

  private showDialog(dialog?: HTMLDialogElement): void {
    if (!dialog || dialog.open) { return; }
    dialog.showModal();
    this.toast.setHeld(true);
  }

  private dismissAiToast(): void {
    if (this.aiToastId) { this.toast.dismiss(this.aiToastId); this.aiToastId = null; }
  }

  // ---------- top bar ----------

  setView(v: View): void {
    this.view = v;
    this.flowMenuOpen = false;
    this.previewOpen = false;
    this.issuesOpen = false;
    this.addMenuOpen = false;
    this.quickAdd = null;
    this.scheduleMeasure();
  }

  preview(fromSelected = false): void {
    this.flowMenuOpen = false;
    this.playScenario = clone(this.scenario);
    this.playStart = fromSelected && this.selected ? this.selected.id : null;
    this.view = 'builder';
    this.previewOpen = true;
    this.bankOpen = false;
  }

  // ---------- saved flows ----------

  showFlows(): void {
    this.buildFlowCards();
    this.setView('flows');
  }

  showCrashCourse(): void {
    if (this.demonstration) { return; }
    this.walkthroughTrigger = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    this.walkthroughOpen = true;
    this.cdr.detectChanges();
    this.showDialog(this.walkthroughDialog?.nativeElement);
  }

  closeWalkthrough(): void {
    this.walkthroughOpen = false;
    this.walkthroughDialog?.nativeElement.close();
    this.walkthroughTrigger?.focus();
    this.walkthroughTrigger = null;
  }

  tryWalkthrough(): void {
    this.closeWalkthrough();
    this.startCrashCourse();
  }

  /** Chapter seeking resets only the isolated demonstration, never the user's editor. */
  resetDemonstration(scenario: Scenario): void {
    if (!this.demonstration) { return; }
    this.hasDraft = false;
    this.savedFlows = [];
    this.savedQuestions = [];
    this.wire = null;
    this.wireD = '';
    this.wireTargetId = null;
    this.drag = null;
    this.pan = null;
    this.spliceEdgeId = null;
    this.spliceOutD = '';
    this.box = null;
    this.boxDrag = null;
    this.resolveConfirmation(false);
    this.libraryDirty = false;
    this.libraryQuestionId = null;
    this.aiTopic = '';
    this.resetAiDraft();
    this.filterLibrary();
    this.openScenario(scenario, null, 'builder');
    this.cdr.detectChanges();
  }

  startCrashCourse(): void {
    this.confirmReplaceDraft(() => {
      this.openScenario(PRACTICE_SCENARIO, null, 'builder');
      this.cdr.detectChanges();
      this.zoomFit();
    });
  }

  /** Blank flows start in Question types: build the questions first, then sequence them in Flow. The course is chosen first. */
  newFlow(): void {
    this.confirmReplaceDraft(() => this.openFlowTarget('new', { scenario: { title: 'Untitled scenario', subtitle: '', steps: [] }, view: 'library' }));
  }

  openFlow(card: FlowCardVm): void {
    if (card.id === this.flowId) { this.setView('builder'); return; }
    this.confirmReplaceDraft(() => this.openScenario(card.flow.scenario, card.id, 'builder'));
  }

  /** Opens a copy; saving it creates a new flow and leaves the template untouched. A copy keeps its course; presets ask for one. */
  useTemplate(scenario: Scenario): void {
    const copy = { ...scenario, title: `${scenario.title} (copy)` };
    this.confirmReplaceDraft(() => copy.target ? this.openScenario(copy, null, 'builder') : this.openFlowTarget('new', { scenario: copy, view: 'builder' }));
  }

  continueDraft(): void { this.setView('builder'); }

  discardDraft(): void {
    this.requestConfirmation({
      title: 'Discard unsaved changes?',
      description: `Unsaved changes to “${this.scenario.title}” will be lost.`,
      actionLabel: 'Discard changes', cancelLabel: 'Keep editing', destructive: true,
    }, () => {
      const saved = this.savedFlows.find(f => f.id === this.flowId);
      this.openScenario(saved ? saved.scenario : { title: 'Untitled scenario', subtitle: '', steps: [] }, saved ? saved.id : null, 'flows');
      if (!saved) {
        if (!this.demonstration) { try { localStorage.removeItem(STORAGE_KEY); } catch { /* nothing stored */ } }
        this.hasDraft = false;
      }
      this.buildFlowCards();
    });
  }

  saveFlow(asNew = false): void {
    if (!this.scenario.steps.length) { return; }
    const id = asNew ? newId() : this.flowId || newId();
    const flow: SavedFlow = { id, scenario: clone(this.scenario), savedAt: Date.now() };
    if (asNew && this.savedFlows.some(f => f.scenario.title === flow.scenario.title)) {
      flow.scenario.title = `${flow.scenario.title} (copy)`;
    }
    const flows = this.savedFlows.some(f => f.id === id) ? this.savedFlows.map(f => f.id === id ? flow : f) : [flow, ...this.savedFlows];
    try { if (!this.demonstration) { localStorage.setItem(FLOWS_KEY, JSON.stringify(flows)); } } catch {
      this.requestConfirmation({
        title: 'Flow not saved',
        description: 'Browser storage is full or unavailable. Your working draft is still open. Try again after making space available.',
        actionLabel: 'Got it',
      });
      return;
    }
    this.savedFlows = flows;
    this.scenario.title = flow.scenario.title;
    this.setFlowId(id);
    this.savedSnapshot = JSON.stringify(this.scenario);
    this.flowDirty = false;
    if (!this.demonstration) {
      this.toast.success(asNew ? 'Saved as a new flow' : 'Flow saved', {
        description: `“${flow.scenario.title}” is saved in this browser.`,
      });
    }
    this.saveLocal();
    this.buildFlowCards();
    if (this.view === 'flows') {
      this.cdr.detectChanges();
      this.newFlowBtn?.nativeElement.focus();
    }
  }

  deleteFlow(card: FlowCardVm): void {
    this.requestConfirmation({
      title: 'Delete saved flow?',
      description: `“${card.title}” will be removed from this browser. Steps already copied into other flows are not affected.`,
      actionLabel: 'Delete flow', cancelLabel: 'Cancel', destructive: true,
    }, () => {
      this.savedFlows = this.savedFlows.filter(f => f.id !== card.id);
      if (!this.demonstration) { try { localStorage.setItem(FLOWS_KEY, JSON.stringify(this.savedFlows)); } catch { /* keep the in-memory list */ } }
      if (this.flowId === card.id) { this.setFlowId(null); this.savedSnapshot = ''; this.flowDirty = true; }
      this.buildFlowCards();
    });
  }

  private confirmReplaceDraft(action: () => void): void {
    if (!this.hasDraft || !this.flowDirty || !this.scenario.steps.length) { action(); return; }
    this.requestConfirmation({
      title: 'Replace unsaved draft?',
      description: `“${this.scenario.title}” has changes that have not been saved. Replacing this draft will discard them.`,
      actionLabel: 'Replace draft', cancelLabel: 'Keep editing', destructive: true,
    }, action);
  }

  private requestConfirmation(confirmation: ScenarioConfirmation, action: (() => void) | null = null): void {
    if (this.confirmation) { return; }
    this.confirmationTrigger = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    this.confirmation = confirmation;
    this.confirmationAction = action;
    // Render the content and focus target before opening the native modal top layer.
    const dialog = this.confirmationDialog?.nativeElement;
    if (dialog && !dialog.open) {
      this.cdr.detectChanges();
      this.showDialog(dialog);
      (this.confirmationCancel || this.confirmationAccept)?.nativeElement.focus();
    }
  }

  resolveConfirmation(accepted: boolean): void {
    if (!this.confirmation) { return; }
    const action = accepted ? this.confirmationAction : null;
    const trigger = this.confirmationTrigger;
    this.confirmationAction = null;
    this.confirmationTrigger = null;
    this.confirmation = null;
    this.confirmationDialog?.nativeElement.close();
    action?.();
    if (this.confirmation) { return; }
    if (action && this.confirmationDialog) { this.cdr.detectChanges(); }
    if (action && document.activeElement === this.libraryHeading?.nativeElement) { return; }
    if (action && this.gateMode) { return; }
    if (trigger?.isConnected && trigger.getClientRects().length) { trigger.focus(); }
    else { (this.flowMenuBtn?.nativeElement || this.newFlowBtn?.nativeElement || this.builderRoot?.nativeElement)?.focus(); }
  }

  cancelConfirmation(event: Event): void {
    event.preventDefault();
    this.resolveConfirmation(false);
  }

  confirmationClosed(): void {
    // A queued close event from the previous message must not cancel a newly opened dialog.
    if (!this.confirmationDialog?.nativeElement.open) { this.resolveConfirmation(false); }
  }

  private openScenario(scenario: Scenario, flowId: string | null, view: View): void {
    // A different flow must never receive a draft requested under the previous one.
    this.resetAiDraft();
    this.resetCoachingDraft();
    this.scenario = clone(scenario);
    this.setFlowId(flowId);
    this.savedSnapshot = flowId ? JSON.stringify(this.scenario) : '';
    this.history = [];
    this.historyAcs = [];
    this.historyIndex = -1;
    this.lastRecord = 0;
    this.previewOpen = false;
    this.setSelection([]);
    this.pendingAcs = scenario.acsCodes ? [...scenario.acsCodes] : null;
    this.pendingAcsCourseId = this.pendingAcs ? scenario.target?.courseId : undefined;
    // Start the target's ACS load first, so the opening history entry records unresolved picks.
    this.syncTarget();
    this.changed();
    this.setView(view);
    this.nextTargetAcsSource = undefined;
  }

  // ---------- course & module gate ----------

  /** Opens the target in 'change' mode, prefilled; from the Draft with AI line and Flow > Course & module. */
  changeFlowTarget(): void { this.openFlowTarget('change'); }

  private openFlowTarget(mode: 'new' | 'change', pending: { scenario: Scenario; view: View } | null = null): void {
    if (this.gateMode) { return; }
    this.gateTrigger = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    this.gateMode = mode;
    this.gatePending = pending;
    this.gateCompose = mode === 'new' && !!pending && !pending.scenario.steps.length;
    this.gatePrompt = '';
    this.gateStructure = 'ai';
    this.gateError = '';
    this.gateAcsElements = [];
    this.gateAcsCodes = [];
    this.gateAcsNote = '';
    this.gateAcsFailed = false;
    this.gateFormats = QUESTION_KINDS.filter(k => k.group === 'A').map(k => ({ kind: k.kind, label: k.name,
      selected: k.kind === 'multiple-choice' || k.kind === 'true-false-round', count: k.kind === 'multiple-choice' ? 2 : 1 }));
    this.gateDescription = mode === 'new'
      ? this.gateCompose ? 'Describe a scenario. AI drafts the flow and you fine-tune it in the builder.'
        : 'Choose the course and module this scenario is for. AI drafts questions for this course and its ACS codes.'
      : 'AI drafts questions for this course and its ACS codes. Steps and saved questions stay as they are.';
    const target = mode === 'change' ? this.scenario.target : undefined;
    this.gateCourseId = target ? String(target.courseId) : '';
    this.gateModuleId = target && target.moduleId != null ? String(target.moduleId) : '';
    this.refreshGate();
    const dialog = this.flowTargetDialog?.nativeElement;
    if (dialog && !dialog.open) {
      this.cdr.detectChanges();
      this.showDialog(dialog);
      const course = this.gateCourse?.trigger.nativeElement;
      (this.gateCompose ? this.gatePromptInput?.nativeElement
        : course && !course.disabled ? course : this.gateCancel?.nativeElement)?.focus();
    }
  }

  /** A new course rescopes modules to "Any module" plus that course's modules and resets the module to Any. */
  gateCourseChanged(): void {
    if (this.gateGenerating) { return; }
    this.gateModuleId = '';
    this.refreshGate();
    if (this.gateCompose) { this.loadGateAcs(); }
  }

  /** Gate picks never mutate the open flow or its history until generation succeeds. */
  loadGateAcs(): void {
    this.gateAcsRequest?.unsubscribe();
    this.gateAcsSource = undefined;
    this.gateAcsCourseId = undefined;
    this.gateAcsElements = [];
    this.gateAcsCodes = [];
    this.gateAcsNote = '';
    this.gateAcsFailed = false;
    this.gateAcsLoading = false;
    const course = this.aiCourses.find(c => String(c.id) === this.gateCourseId);
    if (course && course.acsTestId == null) {
      this.gateAcsNote = 'This course has no ACS test linked. You can generate without ACS tags.';
    } else if (course) {
      this.gateAcsLoading = true;
      this.gateAcsCourseId = course.id;
      this.gateAcsSource = this.courseAcs(course).pipe(takeUntil(this.destroy$), shareReplay({ bufferSize: 1, refCount: true }));
      this.gateAcsRequest = this.gateAcsSource.subscribe(elements => {
        this.gateAcsElements = elements;
        this.gateAcsLoading = false;
        this.gateAcsNote = elements.length ? '' : 'No ACS codes are available for this course.';
        this.refreshGateGeneration();
      }, () => {
        this.gateAcsLoading = false;
        this.gateAcsFailed = true;
        this.gateAcsNote = 'Could not load ACS codes. Retry, or generate without ACS tags.';
        this.refreshGateGeneration();
      });
    }
    this.refreshGateGeneration();
  }

  private courseAcs(course: ProCourse): Observable<AcsElement[]> {
    return this.demonstration ? of(PRACTICE_AI.acs) : this.quizPro.acsElements(course.acsTestId);
  }

  gateAcsChanged(codes: string[]): void {
    this.gateAcsCodes = this.gateAcsElements.filter(e => codes.includes(e.code)).map(e => e.code);
  }

  removeGateAcs(code: string): void { this.gateAcsChanged(this.gateAcsCodes.filter(c => c !== code)); }

  trackGateAcs(_index: number, code: string): string { return code; }

  trackGateFormat(_index: number, format: ScenarioFlowFormatVm): QuestionKind { return format.kind; }

  setGateFormatCount(format: ScenarioFlowFormatVm, count: number): void {
    format.count = Math.max(1, Math.min(5, Math.floor(Number(count)) || 1));
    this.refreshGateGeneration();
  }

  setGateStructure(structure: ScenarioFlowStructure): void {
    if (this.gateGenerating) { return; }
    this.gateStructure = structure;
    this.refreshGateGeneration();
  }

  refreshGateGeneration(): void {
    this.gateTotal = this.gateFormats.filter(f => f.selected).reduce((sum, f) => sum + f.count, 0);
    this.gateBranchPreview = this.gateStructure !== 'linear';
    this.gateQuestionRouteLabel = `${this.gateTotal} questions${this.gateStructure === 'ai' ? ' · AI-chosen paths'
      : this.gateStructure === 'guided' ? ' · retry paths' : ''}`;
    this.gateStructureNote = this.gateStructure === 'ai' ? 'AI chooses which decisions need coaching and retry paths.'
      : this.gateStructure === 'guided' ? 'Every question gets optional coaching. Correct answers continue; mistakes get coaching and a retry.'
      : 'A straight review: briefing, questions, and debrief. Answers use inline feedback.';
    this.gateBlocker = !this.gateReady ? 'Choose a course to generate.'
      : this.gateAcsLoading ? 'Loading ACS codes…'
      : !this.gateTotal ? 'Select at least one question format.'
      : this.gateTotal > 12 ? 'Choose up to 12 questions per flow.'
      : this.gatePrompt.trim().length < 20 ? 'Describe your scenario in at least 20 characters.' : '';
    this.gateCanGenerate = this.gateCompose && !this.gateBlocker && !this.gateGenerating;
    this.cdr.markForCheck();
  }

  generateFlow(): void {
    this.refreshGateGeneration();
    if (!this.gateCanGenerate || this.gateGenerating) { return; }
    const course = this.aiCourses.find(c => String(c.id) === this.gateCourseId);
    const module = this.aiModules.find(m => String(m.id) === this.gateModuleId && m.courseId === course.id);
    const acs = this.gateAcsElements.filter(e => this.gateAcsCodes.includes(e.code)).map(e => ({ code: e.code, description: e.description }));
    const target: ScenarioTarget = { courseId: course.id, courseName: course.name,
      ...(module ? { moduleId: module.id, moduleName: module.label } : {}) };
    const request: ScenarioFlowRequest = { description: this.gatePrompt.trim(), structure: this.gateStructure,
      target: { course: course.name, ...(module ? { module: module.label } : {}), ...(acs.length ? { acs } : {}) },
      formats: this.gateFormats.filter(f => f.selected).map(f => ({ kind: f.kind, count: f.count })) };
    this.gateGenerating = true;
    this.gateError = '';
    this.refreshGateGeneration();
    this.gateGenerationRequest = this.questionAi.draftFlow(request).pipe(take(1), takeUntil(this.destroy$), finalize(() => {
      this.gateGenerating = false;
      this.refreshGateGeneration();
    })).subscribe(draft => {
      const codes = acs.map(a => a.code);
      const elements = this.gateAcsElements;
      this.resolveFlowTarget('cancel');
      this.nextTargetAcsSource = of(elements);
      this.openScenario({ ...draft.scenario, target, acsCodes: codes }, null, 'builder');
      this.cdr.detectChanges();
      this.zoomFit();
      this.builderRoot?.nativeElement.focus();
      this.toast.success('Flow drafted · not saved', { description: 'Review the questions and paths, then save your flow.' });
    }, (error: HttpErrorResponse) => { this.gateError = scenarioAiError(error); this.cdr.markForCheck(); });
  }

  stopFlowGeneration(): void {
    this.gateGenerationRequest?.unsubscribe();
    this.gateGenerating = false;
    this.refreshGateGeneration();
  }

  gatePromptKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey && !event.isComposing) { event.preventDefault(); this.generateFlow(); }
  }

  useGateSuggestion(suggestion: ScenarioFlowSuggestion): void {
    if (this.gateGenerating || !this.gateSuggestions.includes(suggestion)) { return; }
    this.gatePrompt = suggestion.prompt;
    this.refreshGateGeneration();
    this.gatePromptInput?.nativeElement.focus();
  }

  refreshGateSuggestions(): void {
    const course = this.aiCourses.find(c => String(c.id) === this.gateCourseId);
    const module = course && this.aiModules.find(m => m.courseId === course.id && String(m.id) === this.gateModuleId);
    if (!course || !this.gateReady) { this.gateSuggestions = []; return; }
    const topic = module ? module.label : course.name;
    const context = module ? `${module.label} in the ${course.name} course` : `the ${course.name} course`;
    this.gateSuggestions = [
      { label: `${topic}: practice a decision`,
        prompt: `Build a realistic scenario for ${context}. Have the student apply the material to a practical decision and explain their reasoning.` },
      { label: `${topic}: spot a common mistake`,
        prompt: `Build a scenario for ${context} where the student identifies a common misconception, chooses a response, and receives coaching before trying again.` },
    ];
    this.cdr.markForCheck();
  }

  retryCourses(): void { this.loadAiCourses(); }

  /** Confirm stores the target on the flow; skip opens a new flow without one; cancel opens nothing. */
  resolveFlowTarget(choice: 'confirm' | 'skip' | 'cancel'): void {
    const mode = this.gateMode;
    if (!mode || (choice === 'confirm' && !this.gateReady)) { return; }
    if (this.gateGenerating && choice !== 'cancel') { return; }
    this.gateGenerationRequest?.unsubscribe();
    this.gateAcsLoading = false;
    const pending = this.gatePending, trigger = this.gateTrigger;
    const course = this.aiCourses.find(c => String(c.id) === this.gateCourseId);
    const module = course && this.aiModules.find(m => String(m.id) === this.gateModuleId && m.courseId === course.id);
    const target: ScenarioTarget | undefined = choice === 'confirm' && course
      ? { courseId: course.id, courseName: course.name, ...(module ? { moduleId: module.id, moduleName: module.label } : {}) } : undefined;
    if (mode === 'new' && target?.courseId === this.gateAcsCourseId && !this.gateAcsFailed) {
      this.nextTargetAcsSource = this.gateAcsSource;
    }
    this.gateMode = null;
    this.gatePending = null;
    this.gateTrigger = null;
    this.flowTargetDialog?.nativeElement.close();
    if (mode === 'change' && target) { this.applyTarget(target); }
    if (mode === 'new' && pending && choice !== 'cancel') {
      this.openScenario(target ? { ...pending.scenario, target } : pending.scenario, null, pending.view);
    }
    this.gateAcsRequest?.unsubscribe();
    this.gateAcsSource = undefined;
    this.gateAcsCourseId = undefined;
    if (!this.builderRoot) { return; }
    this.cdr.detectChanges();
    if (mode === 'new' && choice !== 'cancel' && this.view === 'library') { this.libraryHeading?.nativeElement.focus(); return; }
    if (trigger?.isConnected && trigger.getClientRects().length) { trigger.focus(); }
    else { (this.flowMenuBtn?.nativeElement || this.newFlowBtn?.nativeElement || this.builderRoot?.nativeElement)?.focus(); }
  }

  cancelFlowTarget(event: Event): void {
    event.preventDefault();
    if (this.gateGenerating) { this.stopFlowGeneration(); return; }
    this.resolveFlowTarget('cancel');
  }

  flowTargetClosed(): void {
    if (!this.flowTargetDialog?.nativeElement.open) { this.resolveFlowTarget('cancel'); }
  }

  private refreshGate(): void {
    const course = this.aiCourses.find(c => String(c.id) === this.gateCourseId);
    // A stored course that is no longer active must be chosen again.
    if (!course && this.aiCoursesLoaded) { this.gateCourseId = ''; }
    this.gateModuleOptions = [{ id: '', label: 'Any module' },
      ...(course ? this.aiModules.filter(m => m.courseId === course.id).map(m => ({ id: String(m.id), label: m.label })) : [])];
    if (!this.gateModuleOptions.some(o => o.id === this.gateModuleId)) { this.gateModuleId = ''; }
    this.gateReady = !!course && !this.aiCoursesLoading;
    this.refreshGateSuggestions();
    this.gateCanSkip = this.gateMode === 'new' && !!this.aiTargetError && !this.aiCoursesLoading;
    this.refreshGateGeneration();
  }

  /** A changed target is an undoable flow edit; the in-flight draft is dropped and ACS codes reload for the course. */
  private applyTarget(target: ScenarioTarget): void {
    if (target.courseId !== this.scenario.target?.courseId) { delete this.scenario.acsCodes; }
    this.scenario.target = target;
    this.resetAiDraft();
    // As in openScenario, the load starts before the edit is recorded.
    this.syncTarget(true);
    this.changed();
  }

  /**
   * Resolves the flow's stored target against loaded CAP courses: course first, then module (a course change resets the
   * module). Stored names are only shown; resolved live names are never written back, so opening a flow never dirties it.
   */
  private syncTarget(reloadAcs = false): void {
    const t = this.scenario.target;
    const course = t && this.aiCourses.find(c => c.id === t.courseId);
    const module = course && this.aiModules.find(m => m.id === t.moduleId && m.courseId === course.id);
    const courseId = course ? String(course.id) : undefined;
    const moduleId = module ? String(module.id) : undefined;
    // A draft in flight was requested for the previous target; its answer must not land under the new one.
    if (this.aiGenerating && (courseId !== this.aiCourseId || moduleId !== this.aiModuleId)) { this.resetAiDraft(); }
    if (reloadAcs || courseId !== this.aiCourseId) {
      this.aiCourseId = courseId;
      this.aiCourseChanged();
    } else if (this.pendingAcs && !this.aiAcsLoading && this.pendingAcsCourseId === (course ? course.id : undefined)) {
      // While a load is in flight the elements are empty; leave the picks for that load to apply.
      this.aiAcsCodes = this.aiElements.filter(e => this.pendingAcs.includes(e.code)).map(e => e.code);
      this.clearPendingAcs();
    }
    this.aiModuleId = moduleId;
    const moduleStale = !!t && t.moduleId != null && !!course && !module;
    this.targetSet = !!t;
    this.targetStale = !!t && this.aiCoursesLoaded && !course;
    this.targetLabel = t ? `${t.courseName} · ${t.moduleName || 'Any module'}` : 'No course';
    this.targetLine = !t ? 'This flow has no course yet.'
      : this.targetStale ? `${t.courseName} is no longer available. Choose another course to draft with AI.`
      : `Drafting for ${t.courseName} · ${moduleStale || !t.moduleName ? 'Any module' : t.moduleName}`;
    this.targetNote = moduleStale ? `${t.moduleName} is no longer in this course; AI drafts for the whole course.` : '';
    this.targetAction = t ? 'Change' : 'Choose course & module';
    this.refreshAiTarget();
  }

  private setFlowId(id: string | null): void {
    this.flowId = id;
    if (this.demonstration) { return; }
    try { if (id) { localStorage.setItem(DRAFT_FLOW_KEY, id); } else { localStorage.removeItem(DRAFT_FLOW_KEY); } } catch { /* session-only */ }
  }

  private buildFlowCards(): void {
    this.flowCards = [...this.savedFlows].sort((a, b) => b.savedAt - a.savedAt).map(flow => ({
      flow, id: flow.id, title: flow.scenario.title || 'Untitled scenario', subtitle: flow.scenario.subtitle, meta: flowMeta(flow.scenario),
      current: flow.id === this.flowId && !this.flowDirty,
    }));
    this.draft = this.hasDraft && this.flowDirty && this.scenario.steps.length ? {
      title: this.scenario.title || 'Untitled scenario', meta: flowMeta(this.scenario),
      note: this.flowId ? 'Unsaved changes to a saved flow' : 'Not saved yet',
    } : null;
  }

  // ---------- flow actions menu ----------

  toggleFlowMenu(focus: 'first' | 'last' = 'first'): void {
    if (this.flowMenuOpen) { this.closeFlowMenu(); return; }
    this.issuesOpen = false;
    this.addMenuOpen = false;
    this.quickAdd = null;
    this.flowMenuLevel = 'root';
    this.flowMenuOpen = true;
    this.cdr.detectChanges();
    const items = this.flowMenuItems();
    items[focus === 'last' ? items.length - 1 : 0]?.focus();
  }

  closeFlowMenu(returnFocus = true): void {
    if (!this.flowMenuOpen) { return; }
    this.flowMenuOpen = false;
    if (returnFocus) { this.flowMenuBtn?.nativeElement.focus(); }
  }

  setFlowMenuLevel(level: 'root' | 'presets'): void {
    this.flowMenuBtn?.nativeElement.focus();
    this.flowMenuLevel = level;
    this.cdr.detectChanges();
    if (level === 'root') { this.flowPresetsItem?.nativeElement.focus(); }
    else { this.flowMenuItems()[0]?.focus(); }
  }

  saveFromFlowMenu(): void {
    if (!this.flowDirty || !this.scenario.steps.length) { return; }
    this.closeFlowMenu();
    this.saveFlow();
  }

  presetCopy(scenario: Scenario): void {
    this.closeFlowMenu();
    this.useTemplate(scenario);
  }

  saveFlowAsNew(): void {
    if (!this.scenario.steps.length) { return; }
    this.closeFlowMenu();
    this.saveFlow(true);
  }

  flowMenuKey(e: KeyboardEvent): void {
    if (e.target === this.flowMenuBtn?.nativeElement) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        this.toggleFlowMenu(e.key === 'ArrowUp' ? 'last' : 'first');
      } else if (e.key === 'Escape') { this.closeFlowMenu(); }
      return;
    }
    if (!this.flowMenuOpen) { return; }
    const items = this.flowMenuItems();
    const i = items.indexOf(document.activeElement as HTMLElement);
    let next: number;
    if (e.key === 'ArrowDown') { next = i + 1; }
    else if (e.key === 'ArrowUp') { next = i - 1; }
    else if (e.key === 'Home') { next = 0; }
    else if (e.key === 'End') { next = items.length - 1; }
    else if (e.key === 'Escape' || (e.key === 'ArrowLeft' && this.flowMenuLevel === 'presets')) {
      e.preventDefault();
      if (this.flowMenuLevel === 'presets') { this.setFlowMenuLevel('root'); }
      else { this.closeFlowMenu(); }
      return;
    }
    else if (e.key === 'ArrowRight' && e.target === this.flowPresetsItem?.nativeElement) {
      e.preventDefault(); this.setFlowMenuLevel('presets'); return;
    }
    // Return to the trigger before native Tab advances to the next/previous toolbar control.
    else if (e.key === 'Tab') { this.closeFlowMenu(); return; }
    else { return; }
    e.preventDefault();
    items[(next + items.length) % items.length]?.focus();
  }

  flowMenuFocusOut(e: FocusEvent): void {
    const to = e.relatedTarget as Element | null;
    if (!to || !to.closest('.sb-presets')) { this.closeFlowMenu(false); }
  }

  @HostListener('document:pointerdown', ['$event'])
  flowMenuOutside(e: PointerEvent): void {
    if (this.flowMenuOpen && !(e.target as Element)?.closest?.('.sb-presets')) { this.closeFlowMenu(false); }
  }

  private flowMenuItems(): HTMLElement[] {
    return Array.from(this.flowMenu?.nativeElement.querySelectorAll<HTMLElement>(
      '[role="menuitem"]:not([aria-disabled="true"]), [role="menuitemcheckbox"]') || []);
  }

  toggleIssues(): void { this.issuesOpen = !this.issuesOpen; }
  goToIssue(i: IssueVm): void { this.select(i.stepId); this.issuesOpen = false; }

  toggleBank(): void {
    this.bankOpen = !this.bankOpen;
    if (this.bankOpen && this.layout === 'compact') { this.select(undefined); }
    if (this.bankOpen && !this.bankItems.length && !this.bankLoading) { this.loadBank(true); }
  }

  // ---------- canvas ----------

  // Freeform canvas: pan/zoom via transform (translate + scale), not scrolling, so there are no edges.
  zoomBy(delta: number): void { this.zoomAt(Math.round((this.zoom + delta) * 10) / 10); }

  zoomFit(): void {
    const el = this.viewport?.nativeElement;
    const steps = this.scenario.steps;
    if (!el || !steps.length) { return; }
    const minX = Math.min(...steps.map(s => s.x)), minY = Math.min(...steps.map(s => s.y));
    const w = Math.max(...steps.map(s => s.x + NODE_W)) - minX, h = Math.max(...steps.map(s => s.y + this.heightOf(s.id))) - minY;
    const z = clampZoom(Math.min((el.clientWidth - 80) / w, (el.clientHeight - 80) / h));
    this.zoom = z;
    this.panX = (el.clientWidth - w * z) / 2 - minX * z;
    this.panY = (el.clientHeight - h * z) / 2 - minY * z;
    this.applyView();
  }

  // Zoom keeping the given viewport point (default: center) fixed under the cursor.
  private zoomAt(z: number, cx?: number, cy?: number): void {
    const el = this.viewport?.nativeElement;
    z = clampZoom(z);
    cx = cx ?? (el ? el.clientWidth / 2 : 0);
    cy = cy ?? (el ? el.clientHeight / 2 : 0);
    this.panX = cx - (cx - this.panX) * (z / this.zoom);
    this.panY = cy - (cy - this.panY) * (z / this.zoom);
    this.zoom = z;
    this.applyView();
  }

  private applyView(): void {
    this.canvasTransform = `translate(${this.panX}px, ${this.panY}px) scale(${this.zoom})`;
    this.gridSize = `${20 * this.zoom}px ${20 * this.zoom}px`;
    this.gridPosition = `${this.panX}px ${this.panY}px`;
  }

  // ---------- canvas pointer: pan (middle button), box select (left on empty), context menu ----------
  // Touch on empty canvas: drag pans, pinch zooms, tap deselects, long-press opens quick add (the touch "right-click").

  canvasDown(e: PointerEvent): void {
    if (e.pointerType === 'touch') { this.touchDown(e); return; }
    if (e.button === 1) {
      e.preventDefault();
      (e.currentTarget as Element).setPointerCapture?.(e.pointerId);
      this.pan = { x: e.clientX, y: e.clientY, panX: this.panX, panY: this.panY };
      this.panning = true;
      return;
    }
    if (e.button !== 0 || (e.target as Element).closest('.sb-node, .sb-edge-hit, .sb-edge-del')) { return; }
    this.quickAdd = null;
    (document.activeElement as HTMLElement | null)?.blur?.(); // move focus off side-panel fields so canvas shortcuts work
    (e.currentTarget as Element).setPointerCapture?.(e.pointerId);
    const additive = e.shiftKey || e.metaKey || e.ctrlKey;
    this.boxDrag = { x: e.clientX, y: e.clientY, base: additive ? [...this.selectedIds] : [] };
    if (!additive) { this.setSelection([]); }
  }

  canvasMove(e: PointerEvent): void {
    if (e.pointerType === 'touch') { this.touchMove(e); return; }
    if (this.pan) {
      this.panX = this.pan.panX + e.clientX - this.pan.x;
      this.panY = this.pan.panY + e.clientY - this.pan.y;
      this.applyView();
      return;
    }
    const b = this.boxDrag;
    if (!b) { return; }
    const r = this.viewportRect();
    const x1 = Math.min(b.x, e.clientX), y1 = Math.min(b.y, e.clientY), x2 = Math.max(b.x, e.clientX), y2 = Math.max(b.y, e.clientY);
    this.box = { left: x1 - r.left, top: y1 - r.top, width: x2 - x1, height: y2 - y1 };
    const a = this.toCanvas(x1, y1), z = this.toCanvas(x2, y2);
    const hits = this.scenario.steps
      .filter(s => s.x < z.x && s.x + NODE_W > a.x && s.y < z.y && s.y + this.heightOf(s.id) > a.y)
      .map(s => s.id);
    this.setSelection([...new Set([...b.base, ...hits])]);
  }

  canvasUp(e?: PointerEvent): void {
    if (e?.pointerType === 'touch') { this.touchUp(e); return; }
    this.pan = null;
    this.panning = false;
    this.boxDrag = null;
    this.box = null;
  }

  private touchDown(e: PointerEvent): void {
    if ((e.target as Element).closest('.sb-node, .sb-edge-hit, .sb-edge-del')) { return; }
    (e.currentTarget as Element).setPointerCapture?.(e.pointerId);
    this.touches.set(e.pointerId, { x: e.clientX, y: e.clientY });
    this.quickAdd = null;
    this.addMenuOpen = false;
    if (this.touches.size === 1) {
      (document.activeElement as HTMLElement | null)?.blur?.();
      this.touchGesture = { startX: e.clientX, startY: e.clientY, moved: false, pinched: false, longPressed: false };
      this.pan = { x: e.clientX, y: e.clientY, panX: this.panX, panY: this.panY };
      this.cancelLongPress();
      this.longPressTimer = setTimeout(() => {
        this.longPressTimer = null;
        if (!this.touchGesture || this.touchGesture.moved || this.touches.size !== 1) { return; }
        this.touchGesture.longPressed = true;
        this.pan = null;
        this.openQuickAdd(e.clientX, e.clientY);
        this.cdr.markForCheck();
      }, LONG_PRESS_MS);
    } else if (this.touches.size === 2) {
      this.cancelLongPress();
      this.pan = null;
      if (this.touchGesture) { this.touchGesture.pinched = true; }
      this.pinch = this.pinchState();
    }
  }

  private touchMove(e: PointerEvent): void {
    if (!this.touches.has(e.pointerId)) { return; }
    this.touches.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const g = this.touchGesture;
    if (g && !g.moved && Math.hypot(e.clientX - g.startX, e.clientY - g.startY) > TAP_SLOP) {
      g.moved = true;
      this.cancelLongPress();
    }
    if (this.pinch && this.touches.size >= 2) {
      // Incremental pinch: scale about the finger midpoint, then follow the midpoint so two-finger drag also pans.
      const next = this.pinchState();
      const r = this.viewportRect();
      this.zoomAt(this.zoom * next.dist / this.pinch.dist, next.midX - r.left, next.midY - r.top);
      this.panX += next.midX - this.pinch.midX;
      this.panY += next.midY - this.pinch.midY;
      this.applyView();
      this.pinch = next;
      return;
    }
    if (this.pan) {
      this.panX = this.pan.panX + e.clientX - this.pan.x;
      this.panY = this.pan.panY + e.clientY - this.pan.y;
      this.applyView();
    }
  }

  private touchUp(e: PointerEvent): void {
    if (!this.touches.delete(e.pointerId)) { return; }
    const g = this.touchGesture;
    if (this.touches.size === 1) {
      // Lifting one finger of a pinch: keep panning with the remaining finger from where it is now.
      const [rest] = [...this.touches.values()];
      this.pinch = null;
      this.pan = { x: rest.x, y: rest.y, panX: this.panX, panY: this.panY };
      return;
    }
    if (this.touches.size) { this.pinch = this.pinchState(); return; }
    this.cancelLongPress();
    this.pan = null;
    this.pinch = null;
    this.touchGesture = null;
    if (e.type === 'pointerup' && g && !g.moved && !g.pinched && !g.longPressed) { this.setSelection([]); }
  }

  private pinchState(): { dist: number; midX: number; midY: number } {
    const [a, b] = [...this.touches.values()];
    return { dist: Math.max(1, Math.hypot(a.x - b.x, a.y - b.y)), midX: (a.x + b.x) / 2, midY: (a.y + b.y) / 2 };
  }

  private cancelLongPress(): void {
    if (this.longPressTimer) { clearTimeout(this.longPressTimer); }
    this.longPressTimer = null;
  }

  /** Right-click on empty canvas opens the add menu at that point. */
  canvasMenu(e: MouseEvent): void {
    if ((e.target as Element).closest('.sb-node')) { return; }
    e.preventDefault();
    this.openQuickAdd(e.clientX, e.clientY);
  }

  // Wheel / trackpad scroll pans; ctrl/cmd + wheel (and trackpad pinch) zooms at the cursor.
  canvasWheel(e: WheelEvent): void {
    e.preventDefault();
    if (e.ctrlKey || e.metaKey) {
      const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
      this.zoomAt(this.zoom * Math.exp(-e.deltaY * 0.01), e.clientX - r.left, e.clientY - r.top);
    } else {
      this.panX -= e.deltaX;
      this.panY -= e.deltaY;
      this.applyView();
    }
  }

  // ---------- nodes: select (shift/cmd toggles), group drag with grid snap (alt = free) ----------

  nodeDown(e: PointerEvent, id: string): void {
    if (e.button !== 0 || (e.target as Element).closest('.sb-port, .sb-node__tools, .sb-coaching')) { return; }
    this.quickAdd = null;
    const toggle = e.shiftKey || e.metaKey || e.ctrlKey;
    if (toggle) {
      const ids = new Set(this.selectedIds);
      if (ids.has(id)) { ids.delete(id); } else { ids.add(id); }
      this.setSelection([...ids]);
    } else if (!this.selectedIds.has(id)) {
      this.setSelection([id]);
    }
    if (!this.selectedIds.has(id)) { return; }
    (e.currentTarget as Element).setPointerCapture?.(e.pointerId);
    const starts = new Map<string, { x: number; y: number }>();
    this.scenario.steps.filter(s => this.selectedIds.has(s.id)).forEach(s => starts.set(s.id, { x: s.x, y: s.y }));
    this.drag = { id, starts, startX: e.clientX, startY: e.clientY, moved: false, toggled: toggle };
  }

  nodeMove(e: PointerEvent): void {
    const d = this.drag;
    if (!d) { return; }
    if (!d.moved && Math.hypot(e.clientX - d.startX, e.clientY - d.startY) < 4) { return; }
    d.moved = true;
    // Snap the grabbed node, then move the rest of the selection by the same delta so relative spacing is kept.
    const grabbed = d.starts.get(d.id) as { x: number; y: number };
    const snap = e.altKey ? 1 : GRID;
    const dx = Math.round((grabbed.x + (e.clientX - d.startX) / this.zoom) / snap) * snap - grabbed.x;
    const dy = Math.round((grabbed.y + (e.clientY - d.startY) / this.zoom) / snap) * snap - grabbed.y;
    d.starts.forEach((start, stepId) => {
      const step = this.find(stepId);
      if (step) { step.x = start.x + dx; step.y = start.y + dy; }
    });
    this.spliceEdgeId = d.starts.size === 1 ? this.spliceTarget(d.id) : null;
    this.rebuild();
  }

  nodeUp(id: string): void {
    const d = this.drag;
    const splice = this.spliceEdgeId;
    this.drag = null;
    this.spliceEdgeId = null;
    if (!d) { return; }
    if (d.moved && splice) { this.splice(d.id, splice); }
    else if (d.moved) { this.buildEdges(); this.persist(); this.cdr.markForCheck(); }
    else if (!d.toggled && this.selectedIds.size > 1) { this.setSelection([id]); }
  }

  /** Connection passing under the dragged card that it could be inserted into, or null. Only unlinked steps with a "next" port qualify. */
  private spliceTarget(id: string): string | null {
    const step = this.find(id);
    const linked = !step || this.portKeys(step).some(port => !!step[port]) ||
      this.scenario.steps.some(s => this.portKeys(s).some(port => s[port] === id));
    if (linked || !this.portKeys(step as ScenarioStep).includes('next')) { return null; }
    // Sample graph wires in canvas coordinates, independent of when Angular renders the SVG preview.
    // ponytail: ~every 12px per wire on each drag frame; index by bounding box if flows get large.
    const x1 = step.x, y1 = step.y, x2 = step.x + NODE_W, y2 = step.y + this.heightOf(id);
    let best: string | null = null, bestHits = 0;
    this.scenario.steps.forEach(from => this.portKeys(from).forEach(port => {
      const to = from[port] && this.find(from[port] as string);
      if (!to) { return; }
      const ax = from.x + NODE_W, ay = from.y + this.portOffset(from.id, port), bx = to.x, by = to.y + IN_Y;
      const wire = this.connectionCurve(from, to, port);
      const samples = Math.max(20, Math.ceil(Math.hypot(bx - ax, by - ay) / 12));
      let hits = 0;
      for (let i = 0; i <= samples; i++) {
        for (const segment of wire.segments || [wire.points]) {
          const p = cubicPoint(segment, i / samples);
          if (p.x >= x1 && p.x <= x2 && p.y >= y1 && p.y <= y2) { hits++; }
        }
      }
      if (hits > bestHits) { bestHits = hits; best = `${from.id}:${port}`; }
    }));
    return best;
  }

  /** A --port--> B becomes A --port--> step --next--> B. */
  private splice(id: string, edgeId: string): void {
    const [fromId, port] = edgeId.split(':') as [string, PortKey];
    const from = this.find(fromId), step = this.find(id);
    const to = from?.[port];
    if (!from || !step || !to) { this.persist(); return; }
    this.scenario.steps.forEach(s => (['next', 'onCorrect', 'onIncorrect'] as PortKey[])
      .filter(key => !this.portKeys(s).includes(key) && s[key] === id).forEach(key => { s[key] = undefined; }));
    from[port] = id;
    step.next = to;
    this.changed();
  }

  // ---------- ports and wires: drag an output onto a node to connect; drop on empty canvas to add a connected step ----------

  portDown(e: PointerEvent, id: string, port: PortKey): void {
    if (e.button !== 0) { return; }
    e.stopPropagation();
    if (e.isTrusted) { (e.currentTarget as Element).setPointerCapture?.(e.pointerId); }
    this.quickAdd = null;
    this.wire = { from: id, port };
    this.wireColor = PORT_COLORS[port];
    this.portMove(e);
  }

  portMove(e: PointerEvent): void {
    const w = this.wire;
    const from = w && this.find(w.from);
    if (!w || !from) { return; }
    // Native inert suppresses browser hit-testing in the read-only demo; use the same node bounds there.
    const hit = this.demonstration ? Array.from(this.viewport.nativeElement.querySelectorAll<HTMLElement>('.sb-node')).find(node => {
      const rect = node.getBoundingClientRect();
      return e.clientX >= rect.left && e.clientX <= rect.right && e.clientY >= rect.top && e.clientY <= rect.bottom;
    }) : document.elementFromPoint(e.clientX, e.clientY)?.closest('.sb-node') as HTMLElement | null;
    // Self-links are excluded (a plain click on a port would otherwise loop the step to itself).
    this.wireTargetId = hit && hit.dataset.id !== w.from ? hit.dataset.id || null : null;
    const to = this.wireTargetId ? this.find(this.wireTargetId) : undefined;
    const end = to ? { x: to.x, y: to.y + IN_Y } : this.toCanvas(e.clientX, e.clientY);
    this.wireD = curve(from.x + NODE_W, from.y + this.portOffset(from.id, w.port), end.x, end.y).d;
  }

  portUp(e: PointerEvent): void {
    const w = this.wire;
    const target = this.wireTargetId;
    this.wire = null;
    this.wireD = '';
    this.wireTargetId = null;
    if (!w) { return; }
    if (target) { this.connect(w.from, w.port, target); } else { this.openQuickAdd(e.clientX, e.clientY, { id: w.from, port: w.port }); }
  }

  edgeDown(e: PointerEvent, id: string): void {
    if (e.button !== 0) { return; }
    e.stopPropagation();
    this.quickAdd = null;
    this.setSelection([]);
    this.selectedEdge = id;
    this.rebuild();
  }

  deleteEdge(id: string): void {
    const [stepId, port] = id.split(':') as [string, PortKey];
    const step = this.find(stepId);
    if (!step) { return; }
    step[port] = undefined;
    this.selectedEdge = null;
    this.changed();
  }

  private connect(fromId: string, port: PortKey, toId: string): void {
    const from = this.find(fromId);
    if (!from) { return; }
    from[port] = toId;
    this.selectedEdge = `${fromId}:${port}`;
    this.changed();
  }

  // ---------- quick add (drop a wire on empty canvas, or right-click) ----------

  private openQuickAdd(clientX: number, clientY: number, from?: { id: string; port: PortKey }): void {
    const r = this.viewportRect();
    const p = this.toCanvas(clientX, clientY);
    // Keep the menu inside the canvas; its max height (CSS) is min(50vh, canvas - 16px).
    const menuH = Math.min(window.innerHeight * 0.5, r.height - 16);
    const left = Math.max(8, Math.min(clientX - r.left, r.width - QUICK_W - 8));
    const top = Math.max(8, Math.min(clientY - r.top, r.height - menuH - 8));
    this.quickAdd = { left, top, cx: p.x, cy: p.y, from };
    this.addMenuOpen = false;
  }

  quickAddStep(type: StepType): void { this.placeQuick(this.newStep(type)); }

  quickAddSaved(saved: SavedQuestion): void { this.placeQuick(this.newSavedStep(saved)); }

  private placeQuick(step: ScenarioStep): void {
    const q = this.quickAdd;
    if (!q) { return; }
    step.x = Math.round(q.cx / GRID) * GRID;
    step.y = Math.round((q.cy - IN_Y) / GRID) * GRID;
    this.scenario.steps = [...this.scenario.steps, step];
    const from = q.from && this.find(q.from.id);
    if (from && q.from) { from[q.from.port] = step.id; }
    this.quickAdd = null;
    this.setSelection([step.id]);
    this.changed();
  }

  // ---------- node actions ----------

  addStep(type: StepType): void {
    this.insertAfterSelected(this.newStep(type));
    this.addMenuOpen = false;
  }

  setStart(id: string): void {
    const step = this.find(id);
    if (!step) { return; }
    this.scenario.steps = [step, ...this.scenario.steps.filter(s => s !== step)];
    this.changed();
  }

  /** Copies the selection offset by one grid step; links between copied steps point at the copies. */
  duplicateSelected(): void {
    const originals = this.scenario.steps.filter(s => this.selectedIds.has(s.id));
    if (!originals.length) { return; }
    const ids = new Map(originals.map(s => [s.id, newId()] as [string, string]));
    const remap = (t?: string) => (t && ids.get(t)) || t;
    const copies = originals.map(s => ({ ...clone(s), id: ids.get(s.id) as string, x: s.x + GRID * 2, y: s.y + GRID * 2,
      next: remap(s.next), onCorrect: remap(s.onCorrect), onIncorrect: remap(s.onIncorrect) }));
    this.scenario.steps = [...this.scenario.steps, ...copies];
    this.setSelection(copies.map(c => c.id));
    this.changed();
  }

  deleteSelected(): void { this.deleteSteps([...this.selectedIds]); }

  /** Removes steps; plain "next" links are healed through the removed step, branch links are cleared. */
  private deleteSteps(ids: string[]): void {
    if (!ids.length) { return; }
    if (ids.includes(this.coachingGeneratingId)) { this.resetCoachingDraft(); }
    ids.forEach(id => {
      const removed = this.find(id);
      if (!removed) { return; }
      this.scenario.steps = this.scenario.steps.filter(s => s !== removed);
      this.scenario.steps.forEach(s => {
        if (s.next === id) { s.next = removed.next === s.id ? undefined : removed.next; }
        if (s.onCorrect === id) { s.onCorrect = undefined; }
        if (s.onIncorrect === id) { s.onIncorrect = undefined; }
      });
    });
    this.setSelection([]);
    this.changed();
  }

  selectAll(): void { this.setSelection(this.scenario.steps.map(s => s.id)); }

  // ---------- undo / redo ----------

  undo(): void { if (this.historyIndex > 0) { this.historyIndex--; this.restore(); } }

  redo(): void { if (this.historyIndex < this.history.length - 1) { this.historyIndex++; this.restore(); } }

  private restore(): void {
    this.resetCoachingDraft();
    this.scenario = JSON.parse(this.history[this.historyIndex]);
    this.pendingAcs = this.historyAcs[this.historyIndex] ? [...this.historyAcs[this.historyIndex]] : null;
    this.pendingAcsCourseId = this.pendingAcs ? this.scenario.target?.courseId : undefined;
    this.lastRecord = 0;
    this.selectedEdge = null;
    this.saveLocal();
    this.syncTarget();
    this.setSelection([...this.selectedIds].filter(id => this.find(id)));
    this.updateHistoryFlags();
    if (this.previewOpen) { this.playScenario = clone(this.scenario); }
  }

  /** Snapshots the flow; edits within 400 ms (typing, nudges) merge into one undo step. */
  private record(): void {
    const json = JSON.stringify(this.scenario);
    if (json === this.history[this.historyIndex]) { return; }
    const now = Date.now();
    this.history = this.history.slice(0, this.historyIndex + 1);
    this.historyAcs = this.historyAcs.slice(0, this.historyIndex + 1);
    const knownPicks = (this.pendingAcs && this.pendingAcsCourseId === Number(this.aiCourseId) ? this.pendingAcs : null)
      || this.inFlightAcsPicks;
    const acsSnapshot = this.aiAcsLoading ? (knownPicks ? [...knownPicks] : null) : [...this.aiAcsCodes];
    if (this.historyIndex > 0 && now - this.lastRecord < 400) { this.history[this.historyIndex] = json; this.historyAcs[this.historyIndex] = acsSnapshot; }
    else { this.history.push(json); this.historyAcs.push(acsSnapshot); }
    if (this.history.length > HISTORY_MAX) { this.history.shift(); this.historyAcs.shift(); }
    this.historyIndex = this.history.length - 1;
    this.lastRecord = now;
    this.updateHistoryFlags();
  }

  private updateHistoryFlags(): void {
    this.canUndo = this.historyIndex > 0;
    this.canRedo = this.historyIndex < this.history.length - 1;
  }

  // ---------- keyboard ----------

  @HostListener('document:keydown', ['$event'])
  onKey(e: KeyboardEvent): void {
    if (this.demonstration || this.walkthroughOpen) { return; }
    // Only when focus is on the page body or the canvas, so typing/buttons in the side panels never delete steps.
    if (this.confirmation || this.gateMode) { return; }
    const target = e.target as HTMLElement | null;
    const onCanvas = !target || target === document.body || !!target.closest?.('.sb-canvas-wrap');
    if (this.view !== 'builder' || this.previewOpen || !onCanvas || target?.closest?.('input, textarea, select, [contenteditable="true"]')) { return; }
    const mod = e.metaKey || e.ctrlKey;
    const key = e.key.toLowerCase();
    let handled = true;
    if (mod && key === 'z') { if (e.shiftKey) { this.redo(); } else { this.undo(); } }
    else if (mod && key === 'y') { this.redo(); }
    else if (mod && key === 'a') { this.selectAll(); }
    else if (mod && key === 'd') { this.duplicateSelected(); }
    else if (key === 'delete' || key === 'backspace') {
      if (this.selectedEdge) { this.deleteEdge(this.selectedEdge); } else { this.deleteSelected(); }
    } else if (key === 'escape') {
      this.quickAdd = null;
      this.addMenuOpen = false;
      this.setSelection([]);
    } else if (NUDGE[e.key] && this.selectedIds.size) {
      const [dx, dy] = NUDGE[e.key];
      const step = e.shiftKey ? GRID : 1;
      this.scenario.steps.filter(s => this.selectedIds.has(s.id)).forEach(s => { s.x += dx * step; s.y += dy * step; });
      this.changed();
    } else { handled = false; }
    if (handled) { e.preventDefault(); this.cdr.markForCheck(); }
  }

  // ---------- mobile settings drawer: drag the handle bar down to dismiss ----------

  sheetDown(e: PointerEvent): void {
    if ((e.target as Element).closest('button')) { return; }
    const el = (e.currentTarget as Element).closest('.sb-settings') as HTMLElement | null;
    if (!el) { return; }
    (e.currentTarget as Element).setPointerCapture?.(e.pointerId);
    el.style.transition = 'none';
    this.sheetDrag = { el, y: e.clientY, t: performance.now(), dy: 0 };
  }

  sheetMove(e: PointerEvent): void {
    const d = this.sheetDrag;
    if (!d) { return; }
    const raw = e.clientY - d.y;
    // Dragging up past the resting position gets heavy resistance instead of a hard stop.
    d.dy = raw < 0 ? raw * 0.15 : raw;
    d.el.style.transform = `translateY(${d.dy}px)`;
  }

  sheetUp(e: PointerEvent): void {
    const d = this.sheetDrag;
    this.sheetDrag = null;
    if (!d) { return; }
    // Clearing inline styles hands control back to the CSS transition, which animates from the dragged position.
    d.el.style.transition = '';
    d.el.style.transform = '';
    const velocity = d.dy / Math.max(1, performance.now() - d.t);
    if (e.type === 'pointerup' && (d.dy > d.el.offsetHeight * 0.3 || (d.dy > 10 && velocity > 0.11))) { this.select(undefined); }
  }

  // ---------- settings ----------

  setInspectorTab(tab: InspectorTab): void {
    if (!this.inspectorTabs.some(item => item.key === tab)) { return; }
    this.inspectorTab = tab;
    if (this.inspectorFamily === 'question') { this.inspectorQuestionTab = tab; }
    else { this.inspectorContentTab = tab; }
    this.inspectorBody?.nativeElement.scrollTo(0, 0);
  }

  inspectorTabKey(event: KeyboardEvent, tab: InspectorTab): void {
    const index = this.inspectorTabs.findIndex(item => item.key === tab);
    const last = this.inspectorTabs.length - 1;
    let next: number;
    if (event.key === 'ArrowRight') { next = (index + 1) % (last + 1); }
    else if (event.key === 'ArrowLeft') { next = (index + last) % (last + 1); }
    else if (event.key === 'Home') { next = 0; }
    else if (event.key === 'End') { next = last; }
    else { return; }
    event.preventDefault();
    this.setInspectorTab(this.inspectorTabs[next].key);
    const tabs = (event.currentTarget as HTMLElement).parentElement?.querySelectorAll<HTMLButtonElement>('[role="tab"]');
    tabs?.[next]?.focus();
  }

  toggleCoaching(id: string): void {
    this.select(id);
    const step = this.selected;
    if (!step || step.type !== 'question') { return; }
    if (!step.coaching) {
      step.coaching = { text: '', after: 'retry' };
      this.coachingOpenId = id;
      this.changed();
    } else {
      this.coachingOpenId = this.coachingOpenId === id ? null : id;
      this.rebuild();
    }
  }

  generateCoaching(id: string): void {
    const step = this.find(id);
    if (this.coachingGeneratingId || !step?.coaching || !step.question?.title?.trim() || this.demonstration) { return; }
    const scenario = this.scenario;
    const coaching = step.coaching;
    const message = coaching.text;
    const question = JSON.stringify(step.question);
    this.coachingGeneratingId = id;
    this.coachingErrorId = null;
    this.coachingError = '';
    this.rebuild();
    this.coachingRequest = this.questionAi.draftCoaching(clone(step.question), scenario.title)
      .pipe(take(1), takeUntil(this.destroy$), finalize(() => {
        this.coachingGeneratingId = null;
        this.rebuild();
      })).subscribe(text => {
        // Switching flows, undo, removal, or typing while waiting must never replace authored content.
        if (this.scenario !== scenario || this.find(id) !== step || step.coaching !== coaching) { return; }
        if (JSON.stringify(step.question) !== question || coaching.text !== message) {
          this.coachingErrorId = id;
          this.coachingError = 'The question or message changed while AI was drafting. Your edits were kept. Generate again.';
          return;
        }
        coaching.text = text;
        this.changed();
      }, (error: HttpErrorResponse) => {
        if (this.scenario !== scenario || this.find(id) !== step || step.coaching !== coaching) { return; }
        this.coachingErrorId = id;
        this.coachingError = scenarioAiError(error);
      });
  }

  private resetCoachingDraft(): void {
    this.coachingRequest?.unsubscribe();
    this.coachingRequest = undefined;
    this.coachingGeneratingId = null;
    this.coachingErrorId = null;
    this.coachingError = '';
  }

  setCoachingAfter(id: string, after: 'retry' | 'continue'): void {
    const step = this.find(id);
    if (!step?.coaching) { return; }
    step.coaching.after = after;
    this.changed();
  }

  removeCoaching(id: string): void {
    if (this.coachingGeneratingId === id) { this.resetCoachingDraft(); }
    if (this.coachingErrorId === id) { this.coachingErrorId = null; this.coachingError = ''; }
    const step = this.find(id);
    if (!step) { return; }
    step.coaching = undefined;
    this.coachingOpenId = null;
    this.changed();
  }

  closeCoaching(): void {
    this.coachingOpenId = null;
    this.rebuild();
  }

  setInspectorBranching(enabled: boolean): void {
    const step = this.selected;
    if (!step || step.type !== 'question') { return; }
    this.inspectorBranching = enabled;
    if (!enabled) { step.onCorrect = undefined; step.onIncorrect = undefined; }
    this.changed();
  }

  showInspectorImage(url: string, alt = ''): void {
    this.inspectorImageUrl = url;
    this.inspectorImageAlt = alt;
    this.showDialog(this.inspectorImageDialog?.nativeElement);
  }

  applyInspectorJson(dialog: HTMLDialogElement): void {
    this.applyJson();
    if (!this.jsonError) { dialog.close(); }
  }

  openInspectorJson(dialog: HTMLDialogElement): void {
    this.questionJson = JSON.stringify(this.selected?.question, null, 2) || '';
    this.jsonError = '';
    this.showDialog(dialog);
  }

  select(id: string | undefined): void {
    this.setSelection(id && this.find(id) ? [id] : []);
  }

  /** Single source of selection state: the settings panel edits `selected` only when exactly one step is selected. */
  private setSelection(ids: string[]): void {
    attachScenarioCoaching(this.scenario);
    ids = ids.filter(id => this.find(id));
    const prev = this.selected;
    this.selectedIds = new Set(ids);
    this.selectedEdge = null;
    this.selectionCount = ids.length;
    if (this.selectionCount && this.layout === 'compact') { this.bankOpen = false; }
    this.selected = ids.length === 1 ? this.find(ids[0]) || null : null;
    this.issuesOpen = false;
    this.refreshSelection(this.selected !== prev);
    this.rebuild();
  }

  changed(): void {
    this.rebuild();
    this.refreshSelection(false);
    this.persist();
    if (this.previewOpen) { this.playScenario = clone(this.scenario); }
  }

  addChoice(list: 'choices' | 'whys'): void {
    const q = this.selected?.question;
    if (!q) { return; }
    q[list] = [...(q[list] || []), { text: 'New option' }];
    this.changed();
  }

  removeChoice(list: 'choices' | 'whys', i: number): void {
    const q = this.selected?.question;
    if (!q) { return; }
    q[list] = (q[list] || []).filter((_, j) => j !== i);
    this.changed();
  }

  toggleCorrect(list: 'choices' | 'whys', i: number): void {
    const q = this.selected?.question;
    if (!q || !q[list]) { return; }
    const single = q.kind !== 'multiple-response';
    q[list] = (q[list] || []).map((c, j) => ({ ...c, correct: j === i ? !c.correct || single : single ? false : c.correct }));
    this.changed();
  }

  // ---------- kind-specific question editors (library + inspector share these) ----------

  /** Routes an edit to the library sync or the flow sync, whichever owns the question object. */
  questionChanged(q: ScenarioQuestion): void {
    if (q === this.libraryQuestion) { this.libraryChanged(); } else { this.changed(); }
  }

  // Sequencing / ranked: the items array order IS the answer key (grading expects item === position).
  addItem(q: ScenarioQuestion): void {
    q.items = [...(q.items || []), 'New step'];
    this.questionChanged(q);
  }

  removeItem(q: ScenarioQuestion, i: number): void {
    q.items = (q.items || []).filter((_, j) => j !== i);
    this.questionChanged(q);
  }

  moveItem(q: ScenarioQuestion, i: number, dir: -1 | 1): void {
    const items = [...(q.items || [])];
    const j = i + dir;
    if (j < 0 || j >= items.length) { return; }
    [items[i], items[j]] = [items[j], items[i]];
    q.items = items;
    this.questionChanged(q);
  }

  setItemText(q: ScenarioQuestion, i: number, text: string): void {
    q.items = (q.items || []).map((item, j) => j === i ? text : item);
    this.questionChanged(q);
  }

  // Matching: each pair row is one correct match; the student right column is rotated by the player.
  addPair(q: ScenarioQuestion): void {
    q.pairs = [...(q.pairs || []), { left: '', right: '' }];
    this.questionChanged(q);
  }

  removePair(q: ScenarioQuestion, i: number): void {
    q.pairs = (q.pairs || []).filter((_, j) => j !== i);
    this.questionChanged(q);
  }

  setPairDot(q: ScenarioQuestion, i: number, dot: string): void {
    q.pairs = (q.pairs || []).map((p, j) => j === i ? { ...p, dot: dot || undefined } : p);
    this.questionChanged(q);
  }

  // True/false statements (inspector counterpart of the library* handlers).
  addStatement(q: ScenarioQuestion): void {
    q.statements = [...(q.statements || []), { text: '', answer: true }];
    this.questionChanged(q);
  }

  removeStatement(q: ScenarioQuestion, i: number): void {
    q.statements = (q.statements || []).filter((_, j) => j !== i);
    this.questionChanged(q);
  }

  setStatementAnswer(q: ScenarioQuestion, i: number, answer: boolean): void {
    q.statements = (q.statements || []).map((s, j) => j === i ? { ...s, answer } : s);
    this.questionChanged(q);
  }

  setPanelValue(q: ScenarioQuestion, key: keyof ScenarioPanelState, value: number | null): void {
    const panel = { ...PANEL_DEFAULTS, ...(q.panel || {}) };
    panel[key] = Number.isFinite(value as number) ? (value as number) : 0;
    q.panel = panel;
    this.questionChanged(q);
  }

  setPlays(q: ScenarioQuestion, value: number | null): void {
    q.plays = Number.isFinite(value as number) && (value as number) >= 1 ? Math.floor(value as number) : 1;
    this.questionChanged(q);
  }

  // Radio phraseology: phrases point at elements by index (-1 = distractor), so removals remap.
  addElement(q: ScenarioQuestion): void {
    q.elements = [...(q.elements || []), 'New element'];
    this.questionChanged(q);
  }

  removeElement(q: ScenarioQuestion, i: number): void {
    q.elements = (q.elements || []).filter((_, j) => j !== i);
    q.phrases = (q.phrases || []).map(p => ({ ...p, element: p.element === i ? -1 : p.element > i ? p.element - 1 : p.element }));
    this.questionChanged(q);
  }

  setElementText(q: ScenarioQuestion, i: number, text: string): void {
    q.elements = (q.elements || []).map((element, j) => j === i ? text : element);
    this.questionChanged(q);
  }

  addPhrase(q: ScenarioQuestion): void {
    q.phrases = [...(q.phrases || []), { text: '', element: -1 }];
    this.questionChanged(q);
  }

  removePhrase(q: ScenarioQuestion, i: number): void {
    q.phrases = (q.phrases || []).filter((_, j) => j !== i);
    this.questionChanged(q);
  }

  setPhraseElement(q: ScenarioQuestion, i: number, value: string | undefined): void {
    const element = Number(value);
    q.phrases = (q.phrases || []).map((p, j) => j === i ? { ...p, element: Number.isFinite(element) ? element : -1 } : p);
    this.questionChanged(q);
  }

  private phraseEditorState(q: ScenarioQuestion): { rows: PhraseRowVm[]; options: ScenarioDropdownOption[] } {
    const phrases = q.kind === 'radio-phraseology' ? q.phrases || [] : [];
    return {
      rows: phrases.map((phrase, index) => ({ index, phrase, elementId: String(phrase.element) })),
      options: [
        { id: '-1', label: 'Distractor · non-standard' },
        ...(q.elements || []).map((label, i) => ({ id: String(i), label: label || `Element ${i + 1}` })),
      ],
    };
  }

  // Diversion plot: ids are the join key for correctAirport, so renames/deletes keep it in sync.
  addAirport(q: ScenarioQuestion): void {
    const airports = [...(q.airports || [])];
    let n = airports.length + 1;
    while (airports.some(p => p.id === `KNEW${n}`)) { n++; }
    airports.push({ id: `KNEW${n}`, x: MAP_W / 2, y: MAP_H / 2, note: '' });
    q.airports = airports;
    this.questionChanged(q);
  }

  removeAirport(q: ScenarioQuestion, i: number): void {
    const removed = (q.airports || [])[i];
    q.airports = (q.airports || []).filter((_, j) => j !== i);
    if (removed && q.correctAirport === removed.id) { q.correctAirport = undefined; }
    if (removed?.origin && q.airports.length && !q.airports.some(p => p.origin)) {
      q.airports = q.airports.map((p, j) => j === 0 ? { ...p, origin: true } : p);
      if (q.correctAirport === q.airports[0].id) { q.correctAirport = undefined; }
    }
    this.questionChanged(q);
  }

  renameAirport(q: ScenarioQuestion, i: number, id: string): void {
    const old = (q.airports || [])[i]?.id;
    q.airports = (q.airports || []).map((p, j) => j === i ? { ...p, id } : p);
    if (old && q.correctAirport === old) { q.correctAirport = id; }
    this.questionChanged(q);
  }

  setAirportOrigin(q: ScenarioQuestion, i: number): void {
    q.airports = (q.airports || []).map((p, j) => ({ ...p, origin: j === i }));
    const origin = q.airports[i];
    if (origin && q.correctAirport === origin.id) { q.correctAirport = undefined; }
    this.questionChanged(q);
  }

  setCorrectAirport(q: ScenarioQuestion, i: number): void {
    const airport = (q.airports || [])[i];
    if (!airport || airport.origin) { return; }
    q.correctAirport = airport.id;
    this.questionChanged(q);
  }

  setAirportCoord(q: ScenarioQuestion, i: number, axis: 'x' | 'y', value: number | null): void {
    const max = axis === 'x' ? MAP_W : MAP_H;
    const coord = Number.isFinite(value as number) ? Math.min(max, Math.max(0, value as number)) : 0;
    q.airports = (q.airports || []).map((p, j) => j === i ? { ...p, [axis]: coord } : p);
    this.questionChanged(q);
  }

  // ---------- hotspots ----------

  /** Click on empty stage adds a point there; clicks that land on a pin are ignored. */
  addSpotAt(e: MouseEvent, q = this.selected?.question): void {
    const target = e.target as Element;
    if (!q || (target !== e.currentTarget && target.tagName !== 'IMG')) { return; }
    const spots = q.hotspots || [];
    q.hotspots = [...spots, { label: `Hotspot ${spots.length + 1}`, ...stagePct(e, e.currentTarget as Element) }];
    this.activeSpot = q.hotspots.length - 1;
    this.hotspotChanged(q);
  }

  spotDown(e: PointerEvent, i: number, q = this.selected?.question): void {
    if (!q || e.button !== 0) { return; }
    const pin = e.currentTarget as Element;
    pin.setPointerCapture?.(e.pointerId);
    this.activeSpot = i;
    this.spotDrag = { i, stage: pin.parentElement as Element, moved: false, question: q };
  }

  spotMove(e: PointerEvent): void {
    const d = this.spotDrag;
    const spot = d && d.question.hotspots?.[d.i];
    if (!d || !spot) { return; }
    Object.assign(spot, stagePct(e, d.stage));
    d.moved = true;
    this.cdr.markForCheck();
  }

  spotUp(): void {
    const drag = this.spotDrag;
    this.spotDrag = null;
    if (drag?.moved) { this.hotspotChanged(drag.question); }
  }

  spotKey(e: KeyboardEvent, i: number, q = this.selected?.question): void {
    const spot = q?.hotspots?.[i];
    const d = NUDGE[e.key];
    if (!spot || !d) { return; }
    e.preventDefault();
    const step = e.shiftKey ? 5 : 1;
    spot.x = clampPct(spot.x + d[0] * step);
    spot.y = clampPct(spot.y + d[1] * step);
    this.hotspotChanged(q);
  }

  toggleSpotCorrect(i: number, q = this.selected?.question): void {
    if (!q?.hotspots) { return; }
    const single = q.kind === 'cockpit-control';
    q.hotspots = q.hotspots.map((h, j) => ({ ...h, correct: j === i ? !h.correct || single : single ? false : h.correct }));
    this.hotspotChanged(q);
  }

  removeSpot(i: number, q = this.selected?.question): void {
    if (!q?.hotspots) { return; }
    q.hotspots = q.hotspots.filter((_, j) => j !== i);
    this.activeSpot = -1;
    this.hotspotChanged(q);
  }

  useCockpitPanel(panel: ScenarioCockpitPanel, q: ScenarioQuestion): void {
    if (q.kind !== 'cockpit-control' || q.imageUrl === panel.imageUrl) { return; }
    const correctLabels = new Set((q.hotspots || []).filter(spot => spot.correct).map(spot => spot.label));
    const keepAnswer = panel.hotspots.some(spot => correctLabels.has(spot.label));
    q.imageUrl = panel.imageUrl;
    q.hotspots = panel.hotspots.map(spot => ({ ...spot, correct: keepAnswer ? correctLabels.has(spot.label) : !!spot.correct }));
    this.activeSpot = -1;
    this.hotspotChanged(q);
  }

  hotspotChanged(target: { imageUrl?: string }): void {
    if (target === this.libraryQuestion) { this.libraryChanged(); } else { this.changed(); }
  }

  /** Library question-image URL edits route through the library sync (pick/drop/clear already do). */
  setLibraryImageUrl(url: string): void {
    this.libraryQuestion.imageUrl = url || undefined;
    this.libraryChanged();
  }

  // ---------- images ----------

  setImageTab(tab: ImageTab): void {
    this.imageTab = tab;
    if (tab === 'ai' && this.selected && !this.selected.imagePrompt) { this.suggestImagePrompt(this.selected); }
  }

  setImageLink(step: ScenarioStep, url: string): void {
    step.imageUrl = url.trim() || undefined;
    step.imageAlt = undefined;
    this.changed();
  }

  suggestImagePrompt(step: ScenarioStep): void {
    const body = (step.body || '').replace(/\s+/g, ' ').trim().slice(0, 400);
    step.imagePrompt = `${IMAGE_PROMPT_STYLE[step.type] || IMAGE_PROMPT_STYLE.narrative}. `
      + `Scenario: ${this.scenario.title}. ${this.scenario.subtitle || ''} Step: ${step.title}. ${body} `
      + 'General aviation training illustration, landscape 16:9, natural light, no text, logos, or readable instruments or checklists.';
    this.imagePromptEdited();
  }

  imagePromptEdited(): void {
    this.discardGeneratedImage();
    this.promptCopied = false;
    this.imageMessage = '';
    this.changed();
  }

  copyImagePrompt(step: ScenarioStep): void {
    if (!step.imagePrompt) { return; }
    if (!navigator.clipboard) {
      this.imageMessage = 'Select the prompt text and copy it to your image tool.';
      return;
    }
    navigator.clipboard.writeText(step.imagePrompt).then(() => {
      if (!this.demonstration) {
        this.toast.success('Prompt copied', { description: 'Generate your image, then upload the result here.' });
      }
      if (this.selected !== step) { return; }
      this.promptCopied = true;
      this.imageMessage = '';
      this.cdr.markForCheck();
    }, () => {
      if (this.selected !== step) { return; }
      this.imageMessage = 'Copy was unavailable. Select the prompt text and copy it manually.';
      this.cdr.markForCheck();
    });
  }

  generateStepImage(step: ScenarioStep): void {
    if (this.demonstration) { return; }
    const prompt = (step.imagePrompt || '').trim();
    if (this.imageGenerating || step.type === 'question') { return; }
    if (prompt.length < 10 || prompt.length > 4000) {
      this.imageMessage = 'Enter a prompt between 10 and 4000 characters.';
      return;
    }
    this.discardGeneratedImage();
    this.imageGenerating = true;
    this.imageMessage = 'Generating your image. This may take a couple of minutes.';
    this.images.generate(prompt).pipe(takeUntil(this.destroy$), finalize(() => {
      this.imageGenerating = false;
      this.cdr.markForCheck();
    })).subscribe(result => {
      // Do not attach a late result to another step, a deleted step, or an edited prompt.
      if (this.selected !== step || !this.scenario.steps.includes(step) || (step.imagePrompt || '').trim() !== prompt) { return; }
      this.generatedImageUrl = result.imageDataUrl;
      this.generatedImageStep = step;
      this.imageMessage = 'Review the image, then select Use image to add it to this step.';
    }, (error: HttpErrorResponse) => {
      if (this.selected !== step) { return; }
      this.imageMessage = error.status === 403 ? 'Image generation is available to content administrators.'
        : error.status === 401 ? 'Sign in again to generate an image.'
        : typeof error.error?.detail === 'string' ? error.error.detail
        : 'Image generation is unavailable. Please try again.';
    });
  }

  useGeneratedImage(step: ScenarioStep): void {
    if (this.generatedImageStep !== step || !this.generatedImageUrl) { return; }
    step.imageUrl = this.generatedImageUrl;
    step.imageAlt = undefined;
    this.discardGeneratedImage();
    this.imageMessage = 'Image added. Describe it in the alt text field for screen-reader users.';
    this.changed();
  }

  discardGeneratedImage(): void {
    this.generatedImageUrl = '';
    this.generatedImageStep = null;
  }

  useImageExample(step: ScenarioStep, example: ScenarioImageExample): void {
    step.imageUrl = example.url;
    step.imageAlt = example.alt;
    this.changed();
  }

  imageDrag(e: DragEvent, over: boolean): void {
    e.preventDefault();
    this.imageDragOver = over;
  }

  /** Accepts a dropped image file, or an image dragged from another tab (URL). */
  dropImage(e: DragEvent, target: { imageUrl?: string; imageAlt?: string }): void {
    e.preventDefault();
    this.imageDragOver = false;
    const file = e.dataTransfer?.files?.[0];
    const link = e.dataTransfer?.getData('text/uri-list');
    if (file) { this.readImage(file, target); } else if (link) { target.imageUrl = link.split('\n')[0].trim(); target.imageAlt = undefined; this.hotspotChanged(target); }
  }

  pickImage(e: Event, target: { imageUrl?: string; imageAlt?: string }): void {
    const input = e.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (file) { this.readImage(file, target); }
  }

  clearImage(target: { imageUrl?: string; imageAlt?: string }): void {
    target.imageUrl = undefined;
    target.imageAlt = undefined;
    this.hotspotChanged(target);
  }

  // ponytail: images are inlined as downscaled JPEG data URLs in localStorage (~5 MB total); upload to blob storage when scenarios persist server-side.
  private readImage(file: File, target: { imageUrl?: string; imageAlt?: string }): void {
    if (!file.type.startsWith('image/')) { return; }
    const src = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const scale = Math.min(1, MAX_IMAGE_W / img.naturalWidth);
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(img.naturalWidth * scale);
      canvas.height = Math.round(img.naturalHeight * scale);
      const ctx = canvas.getContext('2d') as CanvasRenderingContext2D;
      ctx.fillStyle = '#fff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      URL.revokeObjectURL(src);
      target.imageUrl = canvas.toDataURL('image/jpeg', 0.85);
      target.imageAlt = undefined;
      this.hotspotChanged(target);
    };
    img.onerror = () => URL.revokeObjectURL(src);
    img.src = src;
  }

  applyJson(): void {
    const step = this.selected;
    if (!step) { return; }
    try {
      const parsed = JSON.parse(this.questionJson);
      if (!parsed || !QUESTION_KINDS.some(k => k.kind === parsed.kind)) { throw new Error('"kind" must be one of the 19 question types.'); }
      step.question = parsed;
      this.jsonError = '';
      this.changed();
    } catch (err) {
      this.jsonError = (err as Error).message;
    }
  }

  // ---------- library ----------

  filterLibrary(group = this.libraryGroup): void {
    this.libraryGroup = group;
    const term = this.librarySearch.trim().toLocaleLowerCase();
    this.libraryGroups = this.kindGroups
      .filter(g => group === 'all' || g.tag === group)
      .map(g => ({ ...g, kinds: g.kinds.filter(k => !term || `${k.name} ${k.note}`.toLocaleLowerCase().includes(term)) }))
      .filter(g => g.kinds.length);
    this.libraryResults = this.libraryGroups.reduce((count, g) => count + g.kinds.length, 0);
    this.librarySavedResults = this.savedQuestions.filter(saved =>
      !term || `${saved.question.title} ${saved.kindName}`.toLocaleLowerCase().includes(term));
    if (term || group !== 'all') { this.libraryGroups.forEach(g => this.collapsedGroups[g.tag] = false); }
  }

  clearLibrarySearch(): void {
    this.librarySearch = '';
    this.filterLibrary();
  }

  onLibraryTabKey(event: KeyboardEvent): void {
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) { return; }
    const target = event.target as HTMLButtonElement;
    const tabs = Array.from((event.currentTarget as HTMLElement).querySelectorAll<HTMLButtonElement>('[role="tab"]'))
      .filter(tab => tab.offsetParent !== null);
    const index = tabs.indexOf(target);
    if (index < 0) { return; }
    event.preventDefault();
    const next = event.key === 'Home' ? 0 : event.key === 'End' ? tabs.length - 1 :
      (index + (event.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length;
    this.libraryPane = tabs[next].dataset.pane as 'types' | 'edit' | 'preview';
    tabs[next].focus();
  }

  chooseLibraryKind(kind: QuestionKind): void {
    this.confirmDiscardLibrary(() => {
      this.pickLibraryKind(kind);
      this.libraryPane = 'edit';
      this.focusLibraryHeading();
    });
  }

  private focusLibraryHeading(): void {
    if (!this.builderRoot) { return; }
    this.cdr.detectChanges();
    this.libraryHeading?.nativeElement.focus();
  }

  private confirmDiscardLibrary(action: () => void): void {
    if (!this.libraryDirty) { action(); return; }
    this.requestConfirmation({
      title: 'Discard question changes?',
      description: 'Your edits to this question have not been saved. Continuing will discard those changes.',
      actionLabel: 'Discard changes', cancelLabel: 'Keep editing', destructive: true,
    }, action);
  }

  toggleLibraryGroup(tag: string): void { this.collapsedGroups[tag] = !this.collapsedGroups[tag]; }

  pickLibraryKind(kind: QuestionKind): void {
    this.libraryKind = kind;
    this.libraryInfo = kindInfo(kind);
    this.libraryHasChoices = CHOICE_KINDS.includes(kind);
    this.libraryHasHotspots = HOTSPOT_KINDS.includes(kind);
    this.libraryMultiple = kind === 'multiple-response';
    this.collapsedGroups[this.libraryInfo.group] = false;
    this.libraryQuestion = sampleQuestion(kind);
    this.libraryQuestionId = null;
    this.librarySaveError = '';
    this.resetAiDraft();
    this.libraryChanged(false);
  }

  editSavedQuestion(saved: SavedQuestion): void {
    this.confirmDiscardLibrary(() => {
      this.libraryQuestionId = saved.id;
      this.libraryKind = saved.question.kind;
      this.libraryInfo = kindInfo(this.libraryKind);
      this.libraryHasChoices = CHOICE_KINDS.includes(this.libraryKind);
      this.libraryHasHotspots = HOTSPOT_KINDS.includes(this.libraryKind);
      this.libraryMultiple = this.libraryKind === 'multiple-response';
      this.libraryPane = 'edit';
      this.libraryQuestion = clone(saved.question);
      this.librarySaveError = '';
      this.resetAiDraft();
      this.libraryChanged(false);
      this.setView('library');
      this.focusLibraryHeading();
    });
  }

  editFlowQuestion(id: string): void {
    const saved = this.savedQuestions.find(item => item.id === id);
    if (saved) { this.editSavedQuestion(saved); }
  }

  saveLibraryQuestion(): void {
    const q = this.libraryQuestion;
    if (q.kind === 'cloze' && (this.clozeErrors.length || !validClozeQuestion(q))) {
      this.librarySaveError = 'Complete every blank with a unique correct answer and fix the answer bank before saving.';
      return;
    }
    if (q.kind === 'true-false-round' && (!Array.isArray(q.statements) || !q.statements.length ||
        q.statements.some(s => !s || typeof s.text !== 'string' || !s.text.trim() || typeof s.answer !== 'boolean'))) {
      this.librarySaveError = 'Add at least one statement, write text for every statement, and select a True or False answer.';
      return;
    }
    if ((q.kind === 'sequencing' || q.kind === 'ranked') &&
        (!Array.isArray(q.items) || q.items.length < 2 || q.items.some(item => typeof item !== 'string' || !item.trim()))) {
      this.librarySaveError = 'Add at least two items and write text for every item, in the correct order.';
      return;
    }
    if (q.kind === 'matching' &&
        (!Array.isArray(q.pairs) || q.pairs.length < 2 || q.pairs.some(p => !p?.left?.trim() || !p?.right?.trim()))) {
      this.librarySaveError = 'Add at least two pairs and fill in both sides of every pair.';
      return;
    }
    if ((q.kind === 'numeric' || q.kind === 'slider' || q.kind === 'chart-lookup') &&
        (!Number.isFinite(q.answer) || (q.tolerance !== undefined && (!Number.isFinite(q.tolerance) || q.tolerance < 0)))) {
      this.librarySaveError = 'Enter a numeric correct answer and a tolerance of zero or more.';
      return;
    }
    if (q.kind === 'slider' &&
        (!Number.isFinite(q.min) || !Number.isFinite(q.max) || (q.min as number) >= (q.max as number) ||
         (q.answer as number) < (q.min as number) || (q.answer as number) > (q.max as number))) {
      this.librarySaveError = 'Set a minimum below the maximum, with the correct answer between them.';
      return;
    }
    if (q.kind === 'timed' && (!Number.isFinite(q.seconds) || (q.seconds as number) < 3)) {
      this.librarySaveError = 'Give the timed decision at least 3 seconds.';
      return;
    }
    if (q.kind === 'panel-read' &&
        (!q.panel || !Object.values(q.panel).every(v => Number.isFinite(v)))) {
      this.librarySaveError = 'Set every panel value (airspeed, altitude, vertical speed, pitch, bank, heading).';
      return;
    }
    if (q.kind === 'radio-phraseology') {
      const elements = q.elements || [];
      const phrases = q.phrases || [];
      if (!elements.length || elements.some(e => !e?.trim()) || !phrases.length || phrases.some(p => !p?.text?.trim()) ||
          phrases.some(p => !Number.isInteger(p.element) || p.element < -1 || p.element >= elements.length) ||
          elements.some((_, ei) => !phrases.some(p => p.element === ei))) {
        this.librarySaveError = 'Name every required element, write every phrase, and give each element at least one phrase.';
        return;
      }
    }
    if (q.kind === 'audio-cue' && !q.audioScript?.trim()) {
      this.librarySaveError = 'Write the broadcast script the student will hear.';
      return;
    }
    if (HOTSPOT_KINDS.includes(q.kind) && !(q.hotspots || []).some(h => h.correct)) {
      this.librarySaveError = q.kind === 'cockpit-control'
        ? 'Mark the one correct control on the panel.'
        : 'Mark at least one hotspot as a real hazard.';
      return;
    }
    if (q.kind === 'diversion-plot') {
      const airports = q.airports || [];
      const ids = airports.map(p => p.id?.trim());
      const correct = airports.find(p => p.id === q.correctAirport);
      if (airports.length < 2 || ids.some(id => !id) || new Set(ids).size !== ids.length ||
          airports.filter(p => p.origin).length !== 1 || !correct || correct.origin ||
          airports.some(p => !Number.isFinite(p.x) || !Number.isFinite(p.y))) {
        this.librarySaveError = 'Add at least two airports with unique IDs, exactly one origin, and a correct alternate that is not the origin.';
        return;
      }
    }
    if (!q.title?.trim() || !Number.isFinite(q.points) || q.points < 0 ||
        (this.libraryHasChoices && (!(q.choices || []).length || !(q.choices || []).some(c => c.correct && c.text?.trim()))) ||
        (q.kind === 'answer-rationale' && !(q.whys || []).some(c => c.correct && c.text?.trim()))) {
      this.librarySaveError = q.kind === 'answer-rationale'
        ? 'Add a question, valid points, a correct action, and a correct rationale.'
        : 'Add a question, valid points, and a correct answer where this type uses choices.';
      return;
    }
    const id = this.libraryQuestionId || newId();
    const question = clone({ ...q, libraryQuestionId: id });
    const saved = { id, question, kindName: kindInfo(question.kind).name };
    const index = this.savedQuestions.findIndex(item => item.id === id);
    if (index < 0) { this.savedQuestions = [...this.savedQuestions, saved]; }
    else { this.savedQuestions = this.savedQuestions.map(item => item.id === id ? saved : item); }
    this.filterLibrary();
    this.libraryQuestionId = id;
    this.libraryQuestion = clone(question);
    this.scenario.steps.forEach(step => {
      if (step.question?.libraryQuestionId === id) {
        step.question = clone(question);
        step.title = question.title;
      }
    });
    this.librarySaveError = this.persistQuestions() ? '' : 'Browser storage is unavailable; this question will be lost after refresh.';
    if (!this.librarySaveError && !this.demonstration) {
      this.toast.success('Question saved', { description: 'Ready to add to the flow.', announce: false });
    }
    this.libraryChanged(false);
    this.changed();
  }

  addSavedQuestion(saved: SavedQuestion): void {
    this.insertAfterSelected(this.newSavedStep(saved));
    this.setView('builder');
  }

  addCurrentSavedQuestion(): void {
    const saved = this.savedQuestions.find(item => item.id === this.libraryQuestionId);
    if (saved && !this.libraryDirty) { this.addSavedQuestion(saved); }
  }

  deleteSavedQuestion(saved: SavedQuestion): void {
    if (this.scenario.steps.some(step => step.question?.libraryQuestionId === saved.id)) {
      this.librarySaveError = 'Remove this question from the flow before deleting it.';
      return;
    }
    this.requestConfirmation({
      title: 'Delete saved question?',
      description: `“${saved.question.title}” will be removed from your question library in this browser.`,
      actionLabel: 'Delete question', cancelLabel: 'Cancel', destructive: true,
    }, () => {
      this.savedQuestions = this.savedQuestions.filter(item => item.id !== saved.id);
      this.filterLibrary();
      if (this.libraryQuestionId === saved.id) { this.pickLibraryKind(saved.question.kind); }
      this.librarySaveError = this.persistQuestions() ? '' : 'Could not update browser storage.';
    });
  }

  libraryChanged(markDirty = true): void {
    if (this.libraryQuestion.kind === 'cloze') {
      const snapshot = this.clozeContentSnapshot();
      if (this.clozeLoadedQuestion !== this.libraryQuestion || this.clozeSnapshot !== snapshot) {
        const loaded = loadClozeEditor(this.libraryQuestion);
        this.clozeText = loaded.text;
        this.clozeDistractors = loaded.distractors.map(text => ({ text }));
        this.refreshClozeEditor();
        this.clozeLoadedQuestion = this.libraryQuestion;
        this.clozeSnapshot = snapshot;
      }
    } else { this.clozeErrors = []; }
    const phraseState = this.phraseEditorState(this.libraryQuestion);
    this.libraryPhraseRows = phraseState.rows;
    this.libraryPhraseOptions = phraseState.options;
    this.libraryHotspotContext = { $implicit: this.libraryQuestion };
    this.libraryPreviewQuestion = clone(this.libraryQuestion);
    this.libraryJson = JSON.stringify(this.libraryQuestion, null, 2);
    this.libraryJsonError = '';
    this.libraryDirty = markDirty;
    this.libraryCanAdd = !!this.libraryQuestionId && !markDirty;
    this.libraryStatus = this.libraryCanAdd ? 'saved' : this.libraryQuestionId ? 'dirty' : 'new';
    this.aiHasContext = !!this.libraryQuestionId || markDirty;
    if (!markDirty) { this.aiDraftMeta = ''; }
  }

  clozeChanged(): void {
    const result = this.refreshClozeEditor();
    this.libraryQuestion.clozeParts = result.clozeParts;
    this.libraryQuestion.terms = result.terms;
    this.libraryQuestion.clozeAnswers = result.clozeAnswers;
    this.clozeLoadedQuestion = this.libraryQuestion;
    this.clozeSnapshot = this.clozeContentSnapshot();
    this.librarySaveError = '';
    this.libraryChanged();
  }

  clozeSetAnswer(index: number, answer: string): void {
    this.clozeText = replaceClozeBlank(this.clozeText, index, answer);
    this.clozeChanged();
  }

  clozeMakeBlank(passage: HTMLTextAreaElement): void {
    const start = passage.selectionStart;
    const end = passage.selectionEnd;
    const selection = this.clozeText.slice(start, end);
    // Do not nest a new marker inside an existing blank.
    const prefix = this.clozeText.slice(0, start);
    if (prefix.lastIndexOf('[[') > prefix.lastIndexOf(']]') || /\[|\]/.test(selection)) { passage.focus(); return; }
    const answer = selection.trim() || 'answer';
    const leading = selection.match(/^\s*/)[0];
    const trailing = selection.trim() ? selection.match(/\s*$/)[0] : '';
    this.clozeText = prefix + leading + `[[${answer}]]` + trailing + this.clozeText.slice(end);
    this.clozeChanged();
    this.cdr.detectChanges();
    passage.focus();
    passage.setSelectionRange(start + leading.length + 2, start + leading.length + 2 + answer.length);
  }

  clozeRemoveBlank(index: number): void {
    this.clozeText = replaceClozeBlank(this.clozeText, index, '', true);
    this.clozeChanged();
  }

  clozeSwapAnswer(index: number, value: string): void {
    if (value === undefined) { return; }
    const distractor = this.clozeDistractors[Number(value)];
    if (!distractor || !this.clozeBlanks[index]) { return; }
    const answer = distractor.text;
    distractor.text = this.clozeBlanks[index].answer;
    this.clozeSetAnswer(index, answer);
  }

  clozeAddDistractor(): void {
    this.clozeDistractors.push({ text: '' });
    this.clozeChanged();
  }

  clozeRemoveDistractor(index: number): void {
    this.clozeDistractors.splice(index, 1);
    this.clozeChanged();
  }

  private clozeContentSnapshot(): string {
    const q = this.libraryQuestion;
    return JSON.stringify([q.clozeParts, q.terms, q.clozeAnswers]);
  }

  private refreshClozeEditor(): ClozeEditorResult {
    const result = buildClozeEditor(this.clozeText, this.clozeDistractors.map(row => row.text));
    // Preserve in-progress spaces in inputs; only the saved answer-bank terms are trimmed.
    this.clozeBlanks = (this.clozeText.match(/\[\[([^\[\]]*)\]\]/g) || []).map(marker => ({ answer: marker.slice(2, -2) }));
    this.clozePreview = result.clozeParts.map(part => typeof part === 'number'
      ? { text: result.answers[part] || 'Empty answer', blank: true, number: part + 1 }
      : { text: part, blank: false, number: 0 });
    this.clozeBank = result.terms;
    this.clozeErrors = result.errors;
    this.clozeSwapOptions = this.clozeDistractors.map((row, index) => ({ id: String(index), label: row.text.trim() }))
      .filter(option => !!option.label);
    return result;
  }

  /** AI fills the editor with a question of the open kind (another version of it when it has content); nothing is kept until Save. */
  draftLibraryQuestion(): void {
    if (this.aiGenerating || !this.libraryCanDraft || !this.aiTargetReady) { return; }
    // Ask before the request, so a late answer never needs a confirmation of its own.
    const unsavedEdits = this.libraryDirty && JSON.stringify(this.libraryQuestion) !== this.aiFilledJson;
    const request = () => {
      const kind = this.libraryKind;
      const sent = JSON.stringify(this.libraryQuestion);
      const target = this.aiTarget();
      this.resetAiDraft();
      this.aiGenerating = true;
      if (!this.demonstration) { this.aiToastId = this.toast.loading('Drafting question…', { announce: false }); }
      // The walkthrough drafts from prepared versions and decides when they arrive, so seeking replays it exactly,
      // the loading state shows first, and nothing leaves the browser.
      const prepared = PRACTICE_AI.drafts[this.aiHasContext ? 1 : 0];
      const draft$: Observable<ScenarioQuestionDraft> = this.demonstration
        ? (this.demonstrationDraft = new Subject<void>()).pipe(take(1),
          map(() => ({ question: clone(prepared), reference: 'Fictional exercise', confidence: null, ai: null })))
        : this.questionAi.draft(kind, this.aiTopic.trim(), this.scenario.title || '', this.libraryQuestion.points || 0,
          this.aiHasContext ? clone(this.libraryQuestion) : undefined, target);
      this.aiRequest = draft$
        .pipe(finalize(() => {
          this.aiGenerating = false;
          if (this.aiToastId && !this.aiDraftMeta) { this.toast.dismiss(this.aiToastId); this.aiToastId = null; }
          this.cdr.markForCheck();
        }), takeUntil(this.destroy$)).subscribe(draft => {
          if (draft.question.kind !== this.libraryKind) { this.dismissAiToast(); return; }
          if (JSON.stringify(this.libraryQuestion) !== sent) {
            this.dismissAiToast();
            this.aiError = 'You edited the question while AI was drafting, so your edits were kept. Draft again to get a new version.';
            return;
          }
          // Keep the saved-question link so Save changes updates it; the author still has to save.
          this.libraryQuestion = clone({ ...draft.question, libraryQuestionId: this.libraryQuestion.libraryQuestionId });
          this.libraryChanged();
          this.aiFilledJson = JSON.stringify(this.libraryQuestion);
          this.aiDraftMeta = ['Drafted by AI', target.acs ? `ACS ${target.acs.map(a => a.code).join(', ')}` : '', draft.reference,
            draft.confidence == null ? '' : `TypeSafe Jev ${confidenceLabel(draft.confidence)} ready`].filter(Boolean).join(' · ');
          if (this.aiToastId) {
            this.toast.update(this.aiToastId, { type: 'success', title: 'Draft ready · not saved',
              description: 'Review it, then select Save question.', announce: this.view !== 'library' });
          }
        }, (err: HttpErrorResponse) => { this.dismissAiToast(); this.aiError = scenarioAiError(err); });
    };
    if (unsavedEdits) { this.confirmDiscardLibrary(request); } else { request(); }
  }

  /** Delivers the pending prepared draft; a draft reset or replaced since then is already unsubscribed and ignores it. */
  finishDemonstrationDraft(): void {
    const pending = this.demonstrationDraft;
    this.demonstrationDraft = undefined;
    pending?.next();
  }

  resetAiDraft(): void {
    this.dismissAiToast();
    this.aiRequest?.unsubscribe();
    this.demonstrationDraft = undefined;
    this.aiGenerating = false;
    this.aiError = '';
    this.aiDraftMeta = '';
    this.aiFilledJson = '';
    this.libraryCanDraft = this.libraryInfo.group !== 'C';
  }

  /** Picker picks belong to the current history entry, so undo restores them with the flow. */
  aiAcsChanged(codes: string[]): void {
    this.aiAcsCodes = codes;
    this.scenario.acsCodes = [...codes];
    if (this.historyIndex >= 0) {
      this.historyAcs[this.historyIndex] = [...codes];
      this.history[this.historyIndex] = JSON.stringify(this.scenario);
    }
    this.saveLocal();
  }

  /** A new course rescopes ACS codes; an ACS load still running for the old course is dropped. */
  private aiCourseChanged(): void {
    this.aiAcsRequest?.unsubscribe();
    this.inFlightAcsPicks = null;
    const course = this.aiCourses.find(c => String(c.id) === this.aiCourseId);
    this.aiModuleId = undefined;
    this.aiAcsCodes = [];
    this.aiElements = [];
    this.aiAcsLoading = false;
    this.aiAcsNote = '';
    if (course && course.acsTestId == null) {
      this.clearPendingAcs();
      this.aiAcsNote = `${course.name} has no ACS test linked, so AI drafts without an ACS code.`;
    } else if (course) {
      // ponytail: ACS codes are course-wide (the course's ACS test); modules carry no ACS link, same as Quiz Builder Pro.
      this.inFlightAcsPicks = this.pendingAcsCourseId === course.id ? this.pendingAcs : null;
      this.clearPendingAcs();
      this.aiAcsLoading = true;
      const source = this.nextTargetAcsSource || this.courseAcs(course);
      this.nextTargetAcsSource = undefined;
      this.aiAcsRequest = source.subscribe(elements => {
        this.aiElements = elements;
        // Picks a restore queued after this load started are newer than the ones captured at its start.
        const restorePicks = (this.pendingAcsCourseId === course.id ? this.pendingAcs : null) || this.inFlightAcsPicks;
        this.clearPendingAcs();
        this.inFlightAcsPicks = null;
        // A restore reapplies its entry's picks; otherwise top 3 weak areas (none when the course has no recorded misses).
        this.aiAcsCodes = restorePicks ? elements.filter(e => restorePicks.includes(e.code)).map(e => e.code)
          : weakAreas(elements, 3).map(e => e.code);
        // Delivery can be synchronous (cached response) before the target change is recorded; only write
        // into an entry whose stored target is exactly the flow's current target.
        if (this.historyIndex >= 0 && this.entryMatchesCurrentTarget(this.historyIndex)) {
          this.historyAcs[this.historyIndex] = [...this.aiAcsCodes];
        }
        this.settleUnresolvedAcs(course.id, this.aiAcsCodes);
        this.aiAcsNote = elements.length ? '' : `No ACS knowledge or risk elements are recorded for ${course.name}.`;
        this.aiAcsLoading = false;
        this.refreshAiTarget();
      }, () => {
        this.clearPendingAcs();
        this.inFlightAcsPicks = null;
        this.settleUnresolvedAcs(course.id, []);
        this.aiAcsLoading = false;
        this.aiAcsNote = 'Could not load ACS codes for this course. AI can still draft for the course, or use Change to retry.';
        this.refreshAiTarget();
      });
    } else {
      this.clearPendingAcs();
    }
    this.refreshAiTarget();
  }

  /** Entries recorded while this course's codes were loading inherit the picks that load settled on. */
  private settleUnresolvedAcs(courseId: number, codes: string[]): void {
    this.historyAcs = this.historyAcs.map((snapshot, i) =>
      snapshot === null && this.entryCourseId(i) === courseId ? [...codes] : snapshot);
  }

  private entryCourseId(index: number): number | undefined {
    try { return (JSON.parse(this.history[index]) as Scenario).target?.courseId; } catch { return undefined; }
  }

  private entryMatchesCurrentTarget(index: number): boolean {
    try {
      const entry = (JSON.parse(this.history[index]) as Scenario).target;
      const current = this.scenario.target;
      return entry?.courseId === current?.courseId && entry?.moduleId === current?.moduleId;
    } catch { return false; }
  }

  private clearPendingAcs(): void {
    this.pendingAcs = null;
    this.pendingAcsCourseId = undefined;
  }

  refreshAiTarget(): void {
    this.aiTargetReady = !!this.aiCourseId && !this.aiAcsLoading;
    this.cdr.markForCheck();
  }

  private loadAiCourses(): void {
    this.aiCoursesLoading = true;
    this.aiTargetError = '';
    this.refreshGate();
    (this.demonstration ? of(PRACTICE_AI.courses) : this.quizPro.courses()).pipe(takeUntil(this.destroy$)).subscribe(data => {
      this.aiCourses = data.courses;
      this.aiModules = data.modules;
      this.aiCourseOptions = data.courses.map(c => ({ id: String(c.id), label: c.name }));
      this.aiCoursesLoading = false;
      this.aiCoursesLoaded = true;
      this.aiTargetError = data.courses.length ? '' : 'No active courses are available to draft for.';
      this.syncTarget();
      this.refreshGate();
    }, () => {
      this.aiCoursesLoading = false;
      this.aiTargetError = 'Could not load courses. Check that you are signed in and the API is reachable.';
      this.refreshAiTarget();
      this.refreshGate();
    });
  }

  /** Names only from loaded CAP metadata, so the AI never sees a code the course's ACS test does not have. */
  private aiTarget(): ScenarioQuestionTarget {
    const course = this.aiCourses.find(c => String(c.id) === this.aiCourseId);
    const module = this.aiModules.find(m => String(m.id) === this.aiModuleId && m.courseId === course.id);
    const acs = this.aiElements.filter(e => this.aiAcsCodes.includes(e.code)).map(e => ({ code: e.code, description: e.description }));
    return { course: course.name, ...(module ? { module: module.label } : {}), ...(acs.length ? { acs } : {}) };
  }

  retryLibraryPreview(): void { this.libraryPreviewQuestion = clone(this.libraryQuestion); }

  libraryAddStatement(): void {
    this.libraryQuestion.statements = [...(this.libraryQuestion.statements || []), { text: '', answer: true }];
    this.libraryChanged();
  }

  libraryRemoveStatement(i: number): void {
    this.libraryQuestion.statements = (this.libraryQuestion.statements || []).filter((_, j) => j !== i);
    this.libraryChanged();
  }

  librarySetStatementAnswer(i: number, answer: boolean): void {
    this.libraryQuestion.statements = (this.libraryQuestion.statements || []).map((s, j) => j === i ? { ...s, answer } : s);
    this.libraryChanged();
  }

  // `whys` = step 2 rationale options of an answer-rationale question; always single-correct.
  libraryAddChoice(list: 'choices' | 'whys' = 'choices'): void {
    this.libraryQuestion[list] = [...(this.libraryQuestion[list] || []), { text: list === 'whys' ? 'New rationale' : 'New option' }];
    this.libraryChanged();
  }

  libraryRemoveChoice(i: number, list: 'choices' | 'whys' = 'choices'): void {
    this.libraryQuestion[list] = (this.libraryQuestion[list] || []).filter((_, j) => j !== i);
    this.libraryChanged();
  }

  libraryToggleCorrect(i: number, list: 'choices' | 'whys' = 'choices'): void {
    const multiple = list === 'choices' && this.libraryQuestion.kind === 'multiple-response';
    this.libraryQuestion[list] = (this.libraryQuestion[list] || []).map((c, j) =>
      ({ ...c, correct: j === i ? !c.correct || !multiple : multiple ? c.correct : false }));
    this.libraryChanged();
  }

  applyLibraryJson(): void {
    try {
      const parsed = JSON.parse(this.libraryJson) as ScenarioQuestion;
      if (!parsed || parsed.kind !== this.libraryKind || typeof parsed.title !== 'string' || !Number.isFinite(parsed.points)) {
        throw new Error('Keep the selected kind, a question title, and numeric points.');
      }
      if (parsed.kind === 'cloze' && !validClozeQuestion(parsed)) {
        throw new Error('Cloze JSON needs numbered blanks, unique nonempty terms, and a valid correct term index for every blank.');
      }
      this.libraryQuestion = parsed;
      this.libraryChanged();
    } catch (err) {
      this.libraryJsonError = (err as Error).message;
    }
  }

  // ---------- quiz bank ----------

  searchBank(term: string): void {
    this.bankTerm = term;
    this.search$.next(term);
  }

  filterBank(typeId: number | null): void {
    this.bankType = typeId;
    this.loadBank(true);
  }

  loadMoreBank(): void { this.loadBank(false); }

  addFromBank(row: BankRowVm): void {
    const question = this.bank.toScenarioQuestion(row.entity, row.addKind);
    const title = (row.entity.QuestionName || kindInfo(row.addKind).name).slice(0, 60);
    this.insertAfterSelected({ id: newId(), type: 'question', title, x: 0, y: 0, question });
  }

  setBankKind(row: BankRowVm, kind: QuestionKind): void { row.addKind = kind; }

  trackById(_: number, item: { id: string | number }): string | number { return item.id; }
  trackByIndex(i: number): number { return i; }

  // ---------- internals ----------

  private loadBank(reset: boolean): void {
    if (this.demonstration) { return; }
    this.bankPage = reset ? 1 : this.bankPage + 1;
    this.bankLoading = true;
    this.bankError = '';
    this.bank.search(this.bankTerm, this.bankType, this.bankPage)
      .pipe(finalize(() => { this.bankLoading = false; this.cdr.markForCheck(); }), takeUntil(this.destroy$))
      .subscribe(page => {
        this.bankItems = reset ? page.items : [...this.bankItems, ...page.items];
        this.bankTotal = page.total;
        this.bankHasMore = page.hasMore;
        this.buildBankRows();
      }, () => { this.bankError = 'Questions could not be loaded. Check that you are signed in with access to the question bank.'; });
  }

  private buildBankRows(): void {
    const inFlow = new Map<number, string>();
    this.scenario.steps.forEach((s, i) => { if (s.question?.bankQuestionId) { inFlow.set(s.question.bankQuestionId, this.code(s, i)); } });
    const prev = new Map(this.bankRows.map(r => [r.id, r.addKind] as [number, QuestionKind]));
    this.bankRows = this.bankItems.map(item => ({
      ...item,
      added: inFlow.has(item.id),
      inFlowLabel: inFlow.has(item.id) ? `In flow · Step ${inFlow.get(item.id)}` : '',
      addKind: prev.get(item.id) || item.kinds[0],
      kindOptions: item.kinds.map(k => kindInfo(k)),
    }));
  }

  private insertAfterSelected(step: ScenarioStep): void {
    const anchor = this.selected || this.scenario.steps[this.scenario.steps.length - 1];
    if (anchor) {
      step.x = anchor.x + 280;
      step.y = anchor.y;
      this.scenario.steps.forEach(existing => { if (existing.x >= step.x) { existing.x += 280; } });
      if (anchor.type !== 'debrief') {
        if (anchor.onCorrect && (anchor.onIncorrect || anchor.coaching)) {
          step.next = anchor.onCorrect;
          if (anchor.coaching && anchor.next === anchor.onCorrect) { anchor.next = step.id; }
          anchor.onCorrect = step.id;
        } else {
          step.next = anchor.next;
          anchor.next = step.id;
        }
      }
    } else {
      step.x = 40; step.y = 260;
    }
    const index = anchor ? this.scenario.steps.indexOf(anchor) + 1 : 0;
    this.scenario.steps = [...this.scenario.steps.slice(0, index), step, ...this.scenario.steps.slice(index)];
    this.setSelection([step.id]);
    this.changed();
  }

  private newStep(type: StepType): ScenarioStep {
    return { id: newId(), type, title: TYPE_LABELS[type], x: 0, y: 0, body: 'Describe what the student sees and hears at this point in the flight.' };
  }

  private newSavedStep(saved: SavedQuestion): ScenarioStep {
    const question = clone(saved.question);
    return { id: newId(), type: 'question', title: question.title, x: 0, y: 0, question };
  }

  private refreshSelection(resetJson = true): void {
    const s = this.selected;
    const idx = s ? this.scenario.steps.indexOf(s) : -1;
    this.selectedCode = s ? this.code(s, idx) : '';
    this.selectedCanGenerateCoaching = !!s?.question?.title?.trim() && !this.demonstration;
    this.selectedKindInfo = s?.question ? kindInfo(s.question.kind) : null;
    this.selectedHasChoices = !!s?.question && CHOICE_KINDS.includes(s.question.kind);
    const selectedPhraseState = s?.question ? this.phraseEditorState(s.question) : { rows: [], options: [] };
    this.selectedPhraseRows = selectedPhraseState.rows;
    this.selectedPhraseOptions = selectedPhraseState.options;
    this.selectedHasWhys = s?.question?.kind === 'answer-rationale';
    this.selectedHotspotContext = { $implicit: s?.question || null };
    this.selectedHasHotspots = !!s?.question && HOTSPOT_KINDS.includes(s.question.kind);
    this.selectedSingleSpot = s?.question?.kind === 'cockpit-control';
    this.selectedImageExamples = s && s.type !== 'question' ? IMAGE_EXAMPLES[s.type] || [] : [];
    this.selectedImageIsDemo = !!s?.imageUrl && IMAGE_EXAMPLE_URLS.has(s.imageUrl);
    this.selectedImageLink = s?.imageUrl && !s.imageUrl.startsWith('data:') ? s.imageUrl : '';
    this.selectedTypeLabel = s ? TYPE_LABELS[s.type] : '';
    if (resetJson) { this.activeSpot = -1; this.promptCopied = false; this.imageMessage = ''; this.discardGeneratedImage(); }

    this.branchOptions = this.scenario.steps
      .filter(x => x !== s)
      .map(x => ({ id: x.id, label: `${this.code(x, this.scenario.steps.indexOf(x))} · ${x.title}` }));
    this.targetOptions = [{ id: undefined, label: 'End of scenario · show results' }, ...this.branchOptions];
    this.branchDropdownOptions = [{ id: undefined, label: 'Same as default' }, ...this.branchOptions];
    this.refreshInspector(resetJson);
    if (resetJson || !this.jsonError) {
      this.questionJson = s?.question ? JSON.stringify(s.question, null, 2) : '';
      this.jsonError = '';
    }
  }

  private refreshInspector(reset: boolean): void {
    const step = this.selected;
    const question = step?.type === 'question' ? step.question : null;
    const family = step?.type === 'question' ? 'question' : 'content';
    if (reset || family !== this.inspectorFamily) {
      this.inspectorFamily = family;
      this.inspectorTab = family === 'question' ? this.inspectorQuestionTab : this.inspectorContentTab;
      this.inspectorBranching = family === 'question' && (step.onCorrect !== undefined || step.onIncorrect !== undefined);
      this.imageDrawerOpen = false;
      this.imageTab = 'upload';
      this.inspectorBody?.nativeElement.scrollTo(0, 0);
      this.inspectorJsonDialog?.nativeElement.close();
      this.inspectorImageDialog?.nativeElement.close();
      this.inspectorImageUrl = '';
    } else if (family === 'question' && (step.onCorrect !== undefined || step.onIncorrect !== undefined)) {
      this.inspectorBranching = true;
    }
    const destination = (id: string | undefined): string => {
      if (!id) { return 'End of scenario'; }
      return this.branchOptions.find(option => option.id === id)?.label || 'Missing step';
    };
    const incorrectDestination = step?.coaching?.after === 'retry' ? 'Coaching → retry this question'
      : destination(step?.onIncorrect || (step?.coaching ? step.onCorrect || step.next : step?.next));
    this.inspectorRoutes = !step ? [] : family === 'question' && this.inspectorBranching ? [
      { label: 'Correct', destination: destination(step.onCorrect || step.next), tone: 'is-success', icon: 'fa-check' },
      { label: 'Incorrect / partial', destination: incorrectDestination, tone: 'is-danger', icon: 'fa-times' },
    ] : [{ label: 'Then', destination: destination(step.next), tone: '', icon: 'fa-arrow-right' }];
    const answers = this.selectedHasHotspots ? question?.hotspots : this.selectedHasChoices ? question?.choices : null;
    const answerWarning = !!answers && (!answers.length || !answers.some(answer => answer.correct)
      || (this.selectedHasWhys && !question.whys?.some(answer => answer.correct)));
    const tabs: InspectorTab[] = ['content', 'paths'];
    this.inspectorTabs = tabs.map(key => ({
      key, label: key === 'content' ? family === 'question' ? 'Question & answers' : 'Content' : 'Paths',
      id: `sb-inspector-tab-${key}`, panelId: `sb-inspector-panel-${key}`,
      count: key === 'content' ? answers?.length || 0 : this.inspectorRoutes.length,
      warn: key === 'content' && (family === 'question' ? answerWarning : !!step?.imageUrl && !step.imageAlt),
      image: key === 'content' && !!(family === 'question' ? question?.imageUrl : step?.imageUrl),
    }));
  }

  private rebuild(): void {
    attachScenarioCoaching(this.scenario);
    const steps = this.scenario.steps;
    if (!steps.some(step => step.id === this.coachingOpenId && step.coaching)) { this.coachingOpenId = null; }
    const incoming = new Set<string>();
    steps.forEach(s => [s.next, s.onCorrect, s.onIncorrect].forEach(t => t && incoming.add(t)));
    this.issues = [];
    const single = this.selectedIds.size === 1;

    this.nodes = steps.map((s, i) => {
      const code = this.code(s, i);
      const q = s.question;
      const problems: string[] = [];
      if (i > 0 && !incoming.has(s.id)) { problems.push(`${s.title} is unreachable — no transition points to it`); }
      if (s.type === 'question' && !q) { problems.push('Question step has no question configured'); }
      if (q && !q.title) { problems.push('Question has no prompt'); }
      if (s.coaching && !s.coaching.text.trim()) { problems.push('Coaching message is empty'); }
      if (q && CHOICE_KINDS.includes(q.kind) && !(q.choices || []).some(c => c.correct)) { problems.push('No choice is marked correct'); }
      if (q && HOTSPOT_KINDS.includes(q.kind) && !(q.hotspots || []).some(h => h.correct)) { problems.push('No hotspot is marked correct'); }
      problems.forEach(text => this.issues.push({ stepId: s.id, code, text }));

      const info = q ? kindInfo(q.kind) : null;
      const selected = this.selectedIds.has(s.id);
      const nextLabel = s.coaching ? s.coaching.after === 'retry' ? 'Correct' : 'Next'
        : s.onCorrect || s.onIncorrect ? 'Otherwise' : 'Any answer';
      return {
        id: s.id, code, ariaLabel: `Step ${code} ${s.title}`, x: s.x, y: s.y, title: s.title, warn: problems.length > 0,
        thumb: s.type === 'question' ? null : s.imageUrl || null,
        question: s.type === 'question', coaching: s.coaching || null,
        coachingOpen: this.coachingOpenId === s.id,
        coachingLabel: s.coaching?.after === 'retry' ? 'Retry' : 'Continue',
        coachingAiLabel: this.coachingGeneratingId === s.id ? 'Drafting…' : s.coaching?.text ? 'Regenerate with AI' : 'Generate with AI',
        coachingAiDisabled: !!this.coachingGeneratingId || !q?.title?.trim() || this.demonstration,
        coachingAiError: this.coachingErrorId === s.id ? this.coachingError : '',
        selected, primary: selected && single, isStart: i === 0,
        typeLabel: info ? info.name : TYPE_LABELS[s.type], icon: STEP_ICONS[s.type] || null, fromBank: !!q?.bankQuestionId, filled: !!info,
        accent: info ? (info.group === 'C' ? '#0a6e38' : info.group === 'B' ? '#4878c4' : ACCENTS.question) : ACCENTS[s.type],
        meta: q ? `${q.points} pts${q.bankQuestionId ? ' · quiz bank' : ''}` : s.type === 'debrief' ? 'Ends scenario' : '',
        ports: this.portKeys(s).map(key => ({
          key, color: key === 'next' && s.coaching?.after === 'retry' ? PORT_COLORS.onCorrect : PORT_COLORS[key], connected: !!s[key], target: s[key] ? `→ ${this.codeOf(s[key] as string)}` : '',
          label: key === 'onCorrect' ? 'Correct' : key === 'onIncorrect' ? 'Incorrect' : s.type !== 'question' ? 'Next' : nextLabel,
        })),
      };
    });

    this.buildEdges();
    this.canvasW = Math.max(1600, ...steps.map(s => s.x + NODE_W + 200));
    this.canvasH = Math.max(700, ...steps.map(s => s.y + 260));
    if (this.bankRows.length) { this.buildBankRows(); }
    if (!this.drag) { this.scheduleMeasure(); }
    this.cdr.markForCheck();
  }

  /** Questions branch on correct/incorrect with "next" as the fallback (hidden once both branches are set); debriefs end the flow. */
  private portKeys(s: ScenarioStep): PortKey[] {
    if (s.type === 'debrief') { return []; }
    if (s.type !== 'question') { return ['next']; }
    if (s.coaching) {
      // Coaching and its retry are local to the question, so they have no canvas port.
      const main: PortKey = s.onCorrect ? 'onCorrect' : 'next';
      return s.coaching.after === 'continue' && s.onIncorrect && s.onIncorrect !== (s.onCorrect || s.next)
        ? [main, 'onIncorrect'] : [main];
    }
    if (!s.onCorrect && !s.onIncorrect && !(this.selected?.id === s.id && this.inspectorBranching)) { return ['next']; }
    return s.onCorrect && s.onIncorrect ? ['onCorrect', 'onIncorrect'] : ['next', 'onCorrect', 'onIncorrect'];
  }

  private buildEdges(): void {
    this.edges = [];
    this.edgeDel = null;
    this.spliceOutD = '';
    this.scenario.steps.forEach(s => this.portKeys(s).forEach(port => {
      const to = s[port] ? this.find(s[port] as string) : undefined;
      if (!to) { return; }
      const id = `${s.id}:${port}`;
      const x1 = s.x + NODE_W, y1 = s.y + this.portOffset(s.id, port), x2 = to.x, y2 = to.y + IN_Y;
      const back = to.y <= s.y && x2 < x1;
      const c = this.connectionCurve(s, to, port);
      const coachedRetry = port === 'next' && s.coaching?.after === 'retry';
      const label = port === 'onCorrect' || coachedRetry ? 'correct' : port === 'onIncorrect' ? 'incorrect'
        : s.coaching ? '' : back ? 'retry' : s.onCorrect || s.onIncorrect ? 'otherwise' : '';
      const selected = id === this.selectedEdge;
      const spliced = id === this.spliceEdgeId ? this.find(this.drag?.id || '') : undefined;
      const preview = spliced ? curve(x1, y1, spliced.x, spliced.y + IN_Y) : c;
      if (spliced) {
        this.spliceOutD = curve(spliced.x + NODE_W, spliced.y + this.portOffset(spliced.id, 'next'), x2, y2).d;
        this.spliceColor = PORT_COLORS[port];
      }
      this.edges.push({ id, d: preview.d, color: coachedRetry ? PORT_COLORS.onCorrect : PORT_COLORS[port], dash: back || port === 'onIncorrect' ? '5 4' : '0', label: spliced ? '' : label,
        lx: preview.mx, ly: preview.my + (back ? 14 : -8), x2: spliced ? spliced.x : x2, y2: spliced ? spliced.y + IN_Y : y2, selected });
      if (selected) { this.edgeDel = { id, x: c.mx, y: c.my }; }
    }));
  }

  /** Downward branches cross the space between cards; retries take the outer return loop. */
  private connectionCurve(from: ScenarioStep, to: ScenarioStep, port: PortKey): ReturnType<typeof curve> {
    const x1 = from.x + NODE_W, y1 = from.y + this.portOffset(from.id, port);
    const x2 = to.x, y2 = to.y + IN_Y;
    const bottom = from.y + this.heightOf(from.id);
    if (x2 < x1 && to.y > bottom + 24) {
      const laneY = (bottom + to.y) / 2;
      const left = x2 - 40;
      const segments = [
        [x1, y1, x1 + 48, y1, x1 + 48, laneY, x1, laneY],
        [x1, laneY, x1 - 40, laneY, left + 40, laneY, left, laneY],
        [left, laneY, left, y2, left, y2, x2, y2],
      ];
      return { d: `M ${x1} ${y1} ` + segments.map(p => `C ${p[2]} ${p[3]}, ${p[4]} ${p[5]}, ${p[6]} ${p[7]}`).join(' '),
        mx: (x1 + left) / 2, my: laneY, points: segments[0], segments };
    }
    return curve(x1, y1, x2, y2, x2 < x1 ? Math.max(bottom, to.y + this.heightOf(to.id)) + 80 : undefined);
  }

  private portOffset(id: string, port: PortKey): number { return this.portY.get(`${id}:${port}`) ?? ANCHOR_Y; }

  private heightOf(id: string): number { return this.nodeH.get(id) ?? 90; }

  /** Port rows move with title wrapping, so edge anchors come from the rendered DOM (layout px, unaffected by zoom). */
  private scheduleMeasure(): void {
    if (this.measureQueued || typeof requestAnimationFrame === 'undefined') { return; }
    this.measureQueued = true;
    requestAnimationFrame(() => {
      this.measureQueued = false;
      const root = this.viewport?.nativeElement;
      if (!root) { return; }
      let dirty = false;
      root.querySelectorAll<HTMLElement>('.sb-node').forEach(el => {
        const id = el.dataset.id as string;
        if (this.nodeH.get(id) !== el.offsetHeight) { this.nodeH.set(id, el.offsetHeight); dirty = true; }
        el.querySelectorAll<HTMLElement>('.sb-port-row').forEach(row => {
          const key = `${id}:${row.dataset.port}`, y = row.offsetTop + row.offsetHeight / 2;
          if (this.portY.get(key) !== y) { this.portY.set(key, y); dirty = true; }
        });
      });
      if (dirty) { this.buildEdges(); this.cdr.markForCheck(); }
    });
  }

  private viewportRect(): DOMRect | { left: number; top: number; width: number; height: number } {
    return this.viewport?.nativeElement.getBoundingClientRect() || { left: 0, top: 0, width: 0, height: 0 };
  }

  private toCanvas(clientX: number, clientY: number): { x: number; y: number } {
    const r = this.viewportRect();
    return { x: (clientX - r.left - this.panX) / this.zoom, y: (clientY - r.top - this.panY) / this.zoom };
  }

  private code(s: ScenarioStep, i: number): string {
    if (s.type === 'remediation') {
      return 'R' + (this.scenario.steps.filter((x, j) => x.type === 'remediation' && j <= i).length);
    }
    return String(i + 1).padStart(2, '0');
  }

  private codeOf(id: string): string {
    const i = this.scenario.steps.findIndex(s => s.id === id);
    return i < 0 ? '?' : this.code(this.scenario.steps[i], i);
  }

  private find(id: string): ScenarioStep | undefined {
    return this.scenario.steps.find(s => s.id === id);
  }


  // ponytail: localStorage per browser, prototype only; move to a Scenario entity when the schema exists.
  private persist(): void {
    this.saveLocal();
    this.record();
  }

  private saveLocal(): void {
    const json = JSON.stringify(this.scenario);
    this.flowDirty = json !== this.savedSnapshot;
    this.hasDraft = true;
    if (this.demonstration) { return; }
    try { localStorage.setItem(STORAGE_KEY, json); } catch { /* storage unavailable: session-only */ }
  }

  private load(): void {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) as Scenario : null;
      if (parsed && Array.isArray(parsed.steps)) {
        this.scenario = parsed;
        this.pendingAcs = parsed.acsCodes ? [...parsed.acsCodes] : null;
        this.pendingAcsCourseId = this.pendingAcs ? parsed.target?.courseId : undefined;
        this.hasDraft = true;
        const occupied = new Set<string>();
        let moved = false;
        this.scenario.steps.forEach(step => {
          while (occupied.has(`${step.x}:${step.y}`)) { step.x += 280; moved = true; }
          occupied.add(`${step.x}:${step.y}`);
        });
        if (moved) { this.persist(); }
      }
    } catch { /* fall back to demo */ }
  }

  private persistQuestions(): boolean {
    if (this.demonstration) { return true; }
    try { localStorage.setItem(QUESTIONS_KEY, JSON.stringify(this.savedQuestions)); return true; } catch { return false; }
  }

  private loadFlows(): void {
    try {
      const raw = localStorage.getItem(FLOWS_KEY);
      const parsed = raw ? JSON.parse(raw) as SavedFlow[] : [];
      if (Array.isArray(parsed)) { this.savedFlows = parsed.filter(f => f && typeof f.id === 'string' && Array.isArray(f.scenario?.steps)); }
      const open = this.savedFlows.find(f => f.id === localStorage.getItem(DRAFT_FLOW_KEY));
      this.flowId = open ? open.id : null;
      this.savedSnapshot = open ? JSON.stringify(open.scenario) : '';
      this.flowDirty = JSON.stringify(this.scenario) !== this.savedSnapshot;
    } catch { /* start with no saved flows */ }
    this.buildFlowCards();
  }

  private loadQuestions(): void {
    try {
      const raw = localStorage.getItem(QUESTIONS_KEY);
      const parsed = raw ? JSON.parse(raw) as SavedQuestion[] : [];
      if (Array.isArray(parsed)) {
        this.savedQuestions = parsed.filter(item => item && typeof item.id === 'string' &&
          QUESTION_KINDS.some(kind => kind.kind === item.question?.kind))
          .map(item => ({ ...item, kindName: kindInfo(item.question.kind).name }));
      }
    } catch { /* start with an empty library */ }
    this.filterLibrary();
  }
}

function clone<T>(v: T): T { return JSON.parse(JSON.stringify(v)); }

function flowMeta(s: Scenario): string {
  const questions = s.steps.filter(step => step.type === 'question').length;
  return `${s.steps.length} step${s.steps.length === 1 ? '' : 's'} · ${questions} question${questions === 1 ? '' : 's'}`;
}

/** Cubic wire from an output port to an input port; `low` routes backward links underneath both nodes. */
function curve(x1: number, y1: number, x2: number, y2: number, low?: number): { d: string; mx: number; my: number; points: number[]; segments?: number[][] } {
  const [c1x, c1y, c2x, c2y] = low === undefined ? [x1 + 60, y1, x2 - 60, y2] : [x1 + 80, low, x2 - 80, low];
  return {
    d: `M ${x1} ${y1} C ${c1x} ${c1y}, ${c2x} ${c2y}, ${x2} ${y2}`,
    mx: (x1 + 3 * c1x + 3 * c2x + x2) / 8,
    my: (y1 + 3 * c1y + 3 * c2y + y2) / 8,
    points: [x1, y1, c1x, c1y, c2x, c2y, x2, y2],
  };
}

function cubicPoint(p: number[], t: number): { x: number; y: number } {
  const u = 1 - t;
  return {
    x: u * u * u * p[0] + 3 * u * u * t * p[2] + 3 * u * t * t * p[4] + t * t * t * p[6],
    y: u * u * u * p[1] + 3 * u * u * t * p[3] + 3 * u * t * t * p[5] + t * t * t * p[7],
  };
}

function clampZoom(z: number): number { return Math.min(1.5, Math.max(0.25, z)); }
function clampPct(v: number): number { return Math.min(100, Math.max(0, Math.round(v * 10) / 10)); }

function stagePct(e: MouseEvent, stage: Element): { x: number; y: number } {
  const r = stage.getBoundingClientRect();
  return { x: clampPct((e.clientX - r.left) / r.width * 100), y: clampPct((e.clientY - r.top) / r.height * 100) };
}

function newId(): string { return 's' + Math.random().toString(36).slice(2, 8); }
